import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowRightOnRectangleIcon,
  ArrowsRightLeftIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  Bars3BottomLeftIcon,
  BookmarkIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FireIcon,
  HomeIcon,
  InformationCircleIcon,
  LinkIcon,
  LightBulbIcon,
  ListBulletIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from '../components/common/BrandLogo';
import { getCurrentUser } from '../services/authService';
import {
  getQuizDatasets,
  getQuizQuestions,
  getQuizTopics,
  gradeQuestion,
  getLearningSummary,
  getReviewQuestions,
  resolveQuizImageUrl,
  submitExam,
} from '../services/quizService';
import {
  getAttempt,
  getAttempts,
  getExamSession,
  saveExamSession,
  startExamSession,
  submitExamSession,
} from '../services/trainingService';
import { getExamQuotaStatus } from '../utils/quotaUtils';
import './QuizApp.css';

const DEFAULT_DURATION_SECONDS = 50 * 60;
const STORAGE_KEY = 'sao_viet_quiz_state_v3';
const EXAM_SHUFFLE_CACHE_PREFIX = 'sv_exam_shuffled_questions_v2';
const EXAM_ORDER_STORAGE_PREFIX = 'sv_exam_order_v2';

const TYPE_LABELS = {
  single_choice: 'Một đáp án',
  multi_select: 'Nhiều đáp án',
  choice_matrix: 'Đúng / Sai',
  dropdown_matrix: 'Danh sách thả xuống',
  drag_drop_classification: 'Nối cặp',
  sequence: 'Sắp xếp',
};

const parseRequiredCountFromText = (text) => {
  const raw = String(text || '');
  const match = raw.match(/(?:chọn|chon)(?:\s+đủ|\s+du)?\s*(\d+)/i);
  if (!match) return null;
  const count = Number(match[1]);
  return Number.isFinite(count) && count > 1 ? count : null;
};

const getChoiceCorrectCount = (question) => {
  const answerModelCorrectIds = question?.answer_model?.correct_choice_ids;
  if (Array.isArray(answerModelCorrectIds) && answerModelCorrectIds.length > 1) {
    return answerModelCorrectIds.length;
  }
  return null;
};

const getChoiceRequiredCount = (question) => {
  const rootRequired = Number(question?.required_count || 0);
  const modelRequired = Number(question?.answer_model?.required_count || 0);
  const textRequired = parseRequiredCountFromText(question?.question_text || question?.prompt?.question);
  const correctRequired = getChoiceCorrectCount(question);
  const required = Math.max(rootRequired, modelRequired, textRequired || 0, correctRequired || 0);
  return required > 1 ? required : null;
};

const getEffectiveInteractionType = (question) => {
  const type = question?.interaction_type;
  if (type === 'single_choice' || type === 'multi_select') {
    const selectionMode = String(question?.answer_model?.selection_mode || question?.selection_mode || '').toLowerCase();
    if (type === 'multi_select' || selectionMode === 'multiple' || getChoiceRequiredCount(question)) {
      return 'multi_select';
    }
    return 'single_choice';
  }
  return type;
};

const normalizeMatrixOption = (option) => String(option || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/đ/g, 'd')
  .replace(/Đ/g, 'D')
  .trim()
  .toLowerCase();

const isSameMatrixOption = (a, b) => {
  const na = normalizeMatrixOption(a);
  const nb = normalizeMatrixOption(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const pos = new Set(['yes', 'co', 'dung', 'true', '1']);
  const neg = new Set(['no', 'khong', 'sai', 'false', '0']);
  if (pos.has(na) && pos.has(nb)) return true;
  if (neg.has(na) && neg.has(nb)) return true;
  return false;
};

const isBinaryChoiceMatrix = (questionOrRows = []) => {
  const rows = Array.isArray(questionOrRows)
    ? questionOrRows
    : (questionOrRows?.answer_model?.rows || []);
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const firstOptions = rows[0]?.options;
  if (!Array.isArray(firstOptions) || firstOptions.length !== 2) return false;
  if (firstOptions.some((opt) => !opt || String(opt).trim().length === 0 || String(opt).trim().length > 35)) {
    return false;
  }
  const firstNorm0 = normalizeMatrixOption(firstOptions[0]);
  const firstNorm1 = normalizeMatrixOption(firstOptions[1]);
  return rows.every((row) => (
    Array.isArray(row.options)
    && row.options.length === 2
    && normalizeMatrixOption(row.options[0]) === firstNorm0
    && normalizeMatrixOption(row.options[1]) === firstNorm1
  ));
};

const shouldUseMatrixDropdown = (question) => question?.interaction_type === 'choice_matrix'
  && !isBinaryChoiceMatrix(question);

const shouldUseClassificationDrop = (question) => {
  if (question?.interaction_type !== 'drag_drop_classification') return false;
  const items = question.answer_model?.items || [];
  const zones = question.answer_model?.zones || [];
  return zones.length > 0 && items.length > zones.length;
};

const getQuestionTypeLabel = (question) => {
  const effectiveType = getEffectiveInteractionType(question);
  if (shouldUseMatrixDropdown(question)) {
    return 'Chọn từ danh sách';
  }
  if (shouldUseClassificationDrop(question)) {
    return 'Phân loại';
  }
  return TYPE_LABELS[effectiveType] || effectiveType;
};

const emptyAnswerFor = (question) => {
  if (!question) return {};
  switch (getEffectiveInteractionType(question)) {
    case 'single_choice':
    case 'multi_select':
      return { selected_choice_ids: [] };
    case 'choice_matrix':
    case 'dropdown_matrix':
      return { rows: {} };
    case 'drag_drop_classification':
      return { mapping: {} };
    case 'sequence':
      return { order: [], sequence_touched: false };
    default:
      return {};
  }
};

const hasAnswer = (question, answer) => {
  if (!question || !answer) return false;
  const effectiveType = getEffectiveInteractionType(question);
  if (effectiveType === 'single_choice' || effectiveType === 'multi_select') {
    return (answer.selected_choice_ids || []).length > 0;
  }
  if (question.interaction_type === 'choice_matrix' || question.interaction_type === 'dropdown_matrix') {
    return Object.values(answer.rows || {}).some((row) => typeof row === 'object'
      ? Object.values(row).some(Boolean)
      : Boolean(row));
  }
  if (question.interaction_type === 'drag_drop_classification') {
    const validItems = question.answer_model?.items || [];
    const mapping = answer.mapping || {};
    return validItems.length > 0
      ? validItems.some((it) => Boolean(mapping[typeof it === 'string' ? it : it.text]))
      : Object.keys(mapping).length > 0;
  }
  if (question.interaction_type === 'sequence') {
    if (answer.sequence_touched) return true;
    const defaultSteps = question.answer_model?.steps || [];
    const order = answer.order || [];
    return order.length === defaultSteps.length && order.some((step, index) => step !== defaultSteps[index]);
  }
  return false;
};

export const shuffleArray = (array, shouldShuffle = true) => {
  if (!Array.isArray(array)) return [];
  const copy = [...array];
  if (!shouldShuffle || copy.length <= 1) return copy;
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  if (copy.length >= 2 && copy.every((item, idx) => item === array[idx])) {
    [copy[0], copy[1]] = [copy[1], copy[0]];
  }
  return copy;
};

export const normalizeQuestion = (q) => {
  if (!q || !q.answer_model) return q;
  const effectiveType = getEffectiveInteractionType(q);
  const am = q.answer_model;
  return {
    ...q,
    interaction_type: effectiveType,
    required_count: getChoiceRequiredCount(q) || q.required_count,
    answer_model: {
      ...am,
      ...(Array.isArray(am.choices) ? { choices: am.choices.map((c) => ({ ...c })) } : {}),
      ...(Array.isArray(am.rows) ? { rows: am.rows.map((r) => ({ ...r })) } : {}),
      ...(Array.isArray(am.items) ? { items: [...am.items] } : {}),
      ...(Array.isArray(am.zones) ? { zones: [...am.zones] } : {}),
      ...(Array.isArray(am.steps) ? { steps: [...am.steps] } : {}),
    },
  };
};

const reorderByKeys = (items, orderedKeys, getKey) => {
  if (!Array.isArray(items) || !items.length) return [];
  if (!Array.isArray(orderedKeys) || !orderedKeys.length) return [...items];
  const byKey = new Map();
  items.forEach((item) => {
    byKey.set(getKey(item), item);
  });
  const used = new Set();
  const result = [];
  orderedKeys.forEach((key) => {
    if (byKey.has(key) && !used.has(key)) {
      result.push(byKey.get(key));
      used.add(key);
    }
  });
  items.forEach((item) => {
    const key = getKey(item);
    if (!used.has(key)) {
      result.push(item);
    }
  });
  return result;
};

export const randomizeQuestion = (q, shouldShuffle = false, savedChoiceOrder = null) => {
  const normalized = normalizeQuestion(q);
  if (!normalized || !normalized.answer_model) return normalized;

  // 100% guarantee: when shouldShuffle is false and no savedChoiceOrder is given, preserve exact original order
  if (!shouldShuffle && !savedChoiceOrder) {
    return normalized;
  }

  const am = { ...normalized.answer_model };

  if (savedChoiceOrder && typeof savedChoiceOrder === 'object') {
    if (Array.isArray(am.choices) && Array.isArray(savedChoiceOrder.choices)) {
      am.choices = reorderByKeys(am.choices, savedChoiceOrder.choices, (c) => c.id);
    }
    if (Array.isArray(am.items) && Array.isArray(savedChoiceOrder.items)) {
      am.items = reorderByKeys(am.items, savedChoiceOrder.items, (it) => (typeof it === 'string' ? it : it.text));
    }
    if (Array.isArray(am.zones) && Array.isArray(savedChoiceOrder.zones)) {
      am.zones = reorderByKeys(am.zones, savedChoiceOrder.zones, (z) => z);
    }
    if (Array.isArray(am.steps) && Array.isArray(savedChoiceOrder.steps)) {
      am.steps = reorderByKeys(am.steps, savedChoiceOrder.steps, (s) => s);
    }
    return {
      ...normalized,
      answer_model: am,
    };
  }

  if (Array.isArray(am.choices) && am.choices.length > 1) {
    am.choices = shuffleArray(am.choices, true);
  }
  if (Array.isArray(am.items) && Array.isArray(am.zones) && am.items.length > 1) {
    if (am.items.length === am.zones.length) {
      const indices = shuffleArray(am.items.map((_, idx) => idx), true);
      am.items = indices.map((idx) => am.items[idx]);
      am.zones = indices.map((idx) => am.zones[idx]);
    } else {
      am.items = shuffleArray(am.items, true);
    }
  }
  if (Array.isArray(am.steps) && am.steps.length > 1 && Array.isArray(am.correct_order) && am.correct_order.length === am.steps.length) {
    let nextSteps = shuffleArray(am.steps, true);
    if (nextSteps.every((step, idx) => step === am.correct_order[idx])) {
      [nextSteps[0], nextSteps[1]] = [nextSteps[1], nextSteps[0]];
    }
    am.steps = nextSteps;
  }

  return {
    ...normalized,
    answer_model: am,
  };
};

export const extractQuestionOrderMeta = (questions) => {
  if (!Array.isArray(questions) || !questions.length) {
    return { questionOrder: [], choiceOrders: {} };
  }
  const questionOrder = [];
  const choiceOrders = {};
  questions.forEach((q) => {
    const qid = q?.id || q?.question_id;
    if (!qid) return;
    questionOrder.push(qid);
    const am = q.answer_model || {};
    const qMeta = {};
    if (Array.isArray(am.choices)) qMeta.choices = am.choices.map((c) => c.id);
    if (Array.isArray(am.rows)) qMeta.rows = am.rows.map((r) => r.id);
    if (Array.isArray(am.items)) qMeta.items = am.items.map((it) => (typeof it === 'string' ? it : it.text));
    if (Array.isArray(am.zones)) qMeta.zones = [...am.zones];
    if (Array.isArray(am.steps)) qMeta.steps = [...am.steps];
    choiceOrders[qid] = qMeta;
  });
  return { questionOrder, choiceOrders };
};

export const randomizeQuestionList = (list, shouldShuffle = false, savedMeta = null) => {
  if (!Array.isArray(list) || !list.length) return [];
  const sorted = [...list].sort((a, b) => (Number(a.question_index) || 0) - (Number(b.question_index) || 0));

  // 100% guarantee: if shouldShuffle is false, strictly preserve question_index 1..50 and original choice order
  if (!shouldShuffle) {
    return sorted.map((q) => normalizeQuestion(q));
  }

  // Restore saved shuffled order for an in-progress session
  if (savedMeta && Array.isArray(savedMeta.questionOrder) && savedMeta.questionOrder.length > 0) {
    const orderedQuestions = reorderByKeys(sorted, savedMeta.questionOrder, (q) => q.id || q.question_id);
    const choiceOrders = savedMeta.choiceOrders || {};
    return orderedQuestions.map((q) => {
      const qid = q.id || q.question_id;
      const qChoiceOrder = choiceOrders[qid];
      return qChoiceOrder ? randomizeQuestion(q, false, qChoiceOrder) : randomizeQuestion(q, true);
    });
  }

  // Fresh shuffle of both question order and choices/items inside each question
  const shuffledQuestions = shuffleArray(sorted, true);
  return shuffledQuestions.map((q) => randomizeQuestion(q, true));
};

const formatTime = (seconds) => {
  const safeSeconds = Math.max(0, seconds);
  const mm = String(Math.floor(safeSeconds / 60)).padStart(2, '0');
  const ss = String(safeSeconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const renderPromptText = (value) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const parts = text.split(/(\bYes\b|\bNo\b|\bTrue\b|\bFalse\b|Đúng|Sai|\(Chọn\s+\d+\)|Chọn\s+\d+)/gi);
  return parts.map((part, index) => {
    if (/^(\bYes\b|\bNo\b|\bTrue\b|\bFalse\b|Đúng|Sai|\(Chọn\s+\d+\)|Chọn\s+\d+)$/i.test(part)) {
      return <strong key={`${part}-${index}`}>{part}</strong>;
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

const shouldInlinePromptContinuation = (contextText, questionText) => {
  const context = String(contextText || '').trim().toLowerCase();
  const question = String(questionText || '').trim().toLowerCase();
  if (!context || !question) return false;
  return /(?:hãy cho biết|hãy chỉ ra|hãy chọn|chọn)$/i.test(context)
    || /^(yes|no|có|không|đúng|sai|nếu)\b/i.test(question);
};

const getBinaryDisplayLabel = (option, rowOptions = null) => {
  if (!option) return option;
  const norm = normalizeMatrixOption(option);
  if (norm === 'yes' || norm === 'true') {
    if (Array.isArray(rowOptions) && rowOptions.some((o) => normalizeMatrixOption(o) === 'co')) {
      return 'Có';
    }
    return 'Đúng';
  }
  if (norm === 'no' || norm === 'false') {
    if (Array.isArray(rowOptions) && rowOptions.some((o) => normalizeMatrixOption(o) === 'khong')) {
      return 'Không';
    }
    return 'Sai';
  }
  return option;
};

const renderInlineMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text || '';
  if (!text.includes('*')) return text;
  const boldParts = text.split(/(\*\*[^*]+?\*\*)/g);
  return boldParts.map((bPart, bIdx) => {
    if (bPart.startsWith('**') && bPart.endsWith('**') && bPart.length >= 4) {
      return <strong key={`b-${bIdx}`}>{bPart.slice(2, -2)}</strong>;
    }
    if (bPart.includes('*')) {
      const italicParts = bPart.split(/(\*[^*]+?\*)/g);
      return (
        <React.Fragment key={`b-${bIdx}`}>
          {italicParts.map((iPart, iIdx) => {
            if (iPart.startsWith('*') && iPart.endsWith('*') && iPart.length >= 2) {
              return <em key={`i-${iIdx}`}>{iPart.slice(1, -1)}</em>;
            }
            return <React.Fragment key={`i-${iIdx}`}>{iPart}</React.Fragment>;
          })}
        </React.Fragment>
      );
    }
    return <React.Fragment key={`b-${bIdx}`}>{bPart}</React.Fragment>;
  });
};

const cleanExplanationText = (text) => {
  if (!text) return '';
  let str = String(text).trim();

  // Strip generic automated test / OCR noise prefixes
  str = str.replace(/^Your answer is (?:NOT )?correct!?[.\s]*/gi, '');
  str = str.replace(/Your Answers\s+(?:Text\s+)?Correct Answers[.\s]*/gi, '');
  str = str.replace(/[\uF00C\uF00D]/g, '');
  str = str.replace(/^(?:Giải thích(?:\s+chi\s+tiết)?|Feedback)\s*[:：]\s*/gi, '');

  return str.trim();
};

const parseStructuredExplanation = (text, question = null) => {
  if (!text) return null;
  const rawText = cleanExplanationText(text, question);
  if (!rawText) return null;

  // Pattern to catch standalone section headers on their own line:
  // "True:", "False:", "Đúng:", "Sai:", "Yes:", "No:", "Có:", "Không:", "True (Đúng):", "False (Sai):", "True (Có):", "False (Không):"
  const sectionHeaderRegex = /(?:^|\n)\s*(True(?:\s*\((?:Đúng|Có)\))?|False(?:\s*\((?:Sai|Không)\))?|Đúng(?:\s*\((?:True|Yes)\))?|Sai(?:\s*\((?:False|No)\))?|Yes(?:\s*\((?:Đúng|Có)\))?|No(?:\s*\((?:Sai|Không)\))?|Có(?:\s*\((?:Yes|True)\))?|Không(?:\s*\((?:No|False)\))?)\s*[:：](?=\s*(?:\r?\n|$))/gi;

  const matches = [];
  let match;
  while ((match = sectionHeaderRegex.exec(rawText)) !== null) {
    matches.push({
      index: match.index,
      fullMatch: match[0],
      header: match[1],
      lastIndex: sectionHeaderRegex.lastIndex
    });
  }

  // If at least one True/False/Đúng/Sai/Yes/No/Có/Không section is found
  if (matches.length > 0) {
    let intro = '';
    const firstMatch = matches[0];
    const preText = rawText.slice(0, firstMatch.index).trim();
    if (preText && !/^(Giải thích(?:\s+chi\s+tiết)?|Feedback)\s*[:：]?$/i.test(preText)) {
      intro = preText;
    }

    const sections = [];
    let standaloneNote = '';

    for (let i = 0; i < matches.length; i++) {
      const current = matches[i];
      const startPos = current.lastIndex;
      const endPos = (i + 1 < matches.length) ? matches[i + 1].index : rawText.length;
      let sectionBody = rawText.slice(startPos, endPos).trim();

      const hLower = current.header.toLowerCase();
      const isPositive = /true|đúng|yes|có/.test(hLower);
      const isYesNoStyle = /yes|no|có|không/.test(hLower);
      const sectionType = isPositive ? 'correct' : 'wrong';
      const label = isYesNoStyle
        ? (isPositive ? 'Đúng' : 'Sai')
        : (isPositive ? 'Đúng' : 'Sai');

      const bullets = [];
      let note = '';

      // Check if there is an explicit "Giải thích:" or "Lưu ý:" in sectionBody
      const noteMatch = sectionBody.match(/(?:\n\s*|\s{2,}|\.\s+)(?:Giải thích(?:\s+chi\s+tiết)?|Lưu ý|Ghi chú)\s*[:：]\s*(.+)$/is);
      if (noteMatch) {
        note = noteMatch[1].trim();
        sectionBody = sectionBody.slice(0, noteMatch.index).trim();
      }

      if (sectionBody.includes('•')) {
        const cleanedBody = sectionBody.replace(/^[•\-*]\s*/, '');
        const parts = cleanedBody.split(/(?:\r?\n\s*|\s+)•\s*/).map(p => p.trim()).filter(Boolean);
        for (let pIdx = 0; pIdx < parts.length; pIdx++) {
          let part = parts[pIdx].replace(/^[•\-*]\s*/, '');
          // For the last part of a section without an explicit note yet, check for sentence boundary followed by explanation
          if (!note && pIdx === parts.length - 1 && sectionType === 'wrong') {
            const splitSentence = part.match(/^([^.?!]+[.?!])\s+((?:AI|Mô hình|Công cụ|Không|Nhiều|Vì|Lưu ý|Giải thích|Cả hai|Việc)[^.?!].+)$/s);
            if (splitSentence && splitSentence[2] && splitSentence[2].length > 15) {
              bullets.push(splitSentence[1].trim());
              note = splitSentence[2].trim();
              continue;
            }
          }
          if (part) bullets.push(part);
        }
      } else {
        const lines = sectionBody.split('\n').map(l => l.trim()).filter(Boolean);
        for (let lIdx = 0; lIdx < lines.length; lIdx++) {
          const l = lines[lIdx].replace(/^[•\-*]\s*/, '');
          if (!note && lIdx === lines.length - 1 && sectionType === 'wrong') {
            const splitSentence = l.match(/^([^.?!]+[.?!])\s+((?:AI|Mô hình|Công cụ|Không|Nhiều|Vì|Lưu ý|Giải thích|Cả hai|Việc)[^.?!].+)$/s);
            if (splitSentence && splitSentence[2] && splitSentence[2].length > 15) {
              bullets.push(splitSentence[1].trim());
              note = splitSentence[2].trim();
              continue;
            }
          }
          if (l) bullets.push(l);
        }
      }

      if (note) {
        standaloneNote = standaloneNote ? `${standaloneNote}\n${note}` : note;
      }

      sections.push({
        type: sectionType,
        label,
        bullets
      });
    }

    return {
      isStructured: true,
      intro,
      sections,
      note: standaloneNote
    };
  }

  // Non-matrix explanation: distinguish prose paragraphs, sub-headings, and actual bullet items
  const rawLines = rawText.split('\n').map((l) => l.trim()).filter(Boolean);
  const expandedLines = [];

  for (const line of rawLines) {
    if (/^(?:Đáp án(?:\s+đúng)?(?:\s+và\s+giải\s+thích)?)\s*[:：]?$/i.test(line)) {
      expandedLines.push({ raw: line, forceGroup: 'answer-header' });
      continue;
    }
    if (/^(?:Giải thích(?:\s+chi\s+tiết)?|Feedback)\s*[:：]?$/i.test(line)) {
      expandedLines.push({ raw: line, forceGroup: 'explanation-header' });
      continue;
    }
    const inlineExpHeaderMatch = line.match(/^(?:Giải thích(?:\s+chi\s+tiết)?|Feedback)\s*[:：]\s*(.+)$/i);
    const workingLine = inlineExpHeaderMatch ? inlineExpHeaderMatch[1].trim() : line;
    if (inlineExpHeaderMatch && expandedLines.length > 0) {
      expandedLines.push({ raw: 'Giải thích:', forceGroup: 'explanation-header' });
    }
    if (!workingLine) continue;

    if (workingLine.includes('•') && workingLine.split('•').length > 2) {
      const startsWithBullet = /^[•\-*]\s*/.test(workingLine);
      const subParts = workingLine.split(/\s*•\s*/).map((p) => p.trim()).filter(Boolean);
      subParts.forEach((sp, sIdx) => {
        if (sIdx === 0 && !startsWithBullet) {
          expandedLines.push({ raw: sp });
        } else {
          expandedLines.push({ raw: `• ${sp.replace(/^[•\-*]\s*/, '')}` });
        }
      });
    } else {
      expandedLines.push({ raw: workingLine });
    }
  }

  const contentLineCount = expandedLines.filter((entry) => !entry.forceGroup).length;
  const items = [];
  let currentGroup = 'explanation';

  for (const entry of expandedLines) {
    const line = entry.raw;
    if (entry.forceGroup === 'answer-header') {
      currentGroup = 'answer';
      items.push({ type: 'subheading', group: 'header', content: line.replace(/\s*[:：]?$/, ':') });
      continue;
    }
    if (entry.forceGroup === 'explanation-header') {
      currentGroup = 'explanation';
      if (items.length > 0) {
        items.push({ type: 'subheading', group: 'header', content: 'Giải thích:' });
      }
      continue;
    }

    const strippedLine = line.replace(/^[•\-*]\s*/, '').trim();
    if (!strippedLine) continue;

    // Sub-heading ending with a colon ":" introducing a list
    if (/[:：]$/.test(strippedLine)) {
      items.push({ type: 'subheading', group: currentGroup, content: strippedLine });
      continue;
    }

    // Single-line explanation is always rendered as cohesive prose (without a bullet dot)
    if (contentLineCount === 1) {
      items.push({ type: 'prose', group: currentGroup, content: strippedLine });
      continue;
    }

    const hasBulletMarker = /^[•\-*]\s+/.test(line) || /^[•]\s*/.test(line);
    const isStatementBreakdown = /^(?:Mệnh đề|Nhận định|Tùy chọn|Ý|Bước|Vị trí|Cặp)\s*\d+\s*(?:\([^)]+\))?\s*[:：]/i.test(strippedLine);

    if (hasBulletMarker || isStatementBreakdown) {
      items.push({ type: 'bullet', group: currentGroup, content: strippedLine });
    } else {
      items.push({ type: 'prose', group: currentGroup, content: strippedLine });
    }
  }

  return {
    isStructured: false,
    items,
  };
};

const splitValidBulletLabel = (text) => {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (trimmed.startsWith('**')) return null;

  const match = trimmed.match(/^([^:：]{2,150}[:：])\s+(.+)$/s);
  if (!match) return null;
  const prefix = match[1].slice(0, -1).trim();
  const rest = match[2].trim();
  if (!prefix || !rest) return null;

  // Reject if parentheses or quotes are unbalanced inside the prefix
  const openParens = (prefix.match(/\(/g) || []).length;
  const closeParens = (prefix.match(/\)/g) || []).length;
  if (openParens !== closeParens) return null;
  const totalQuotes = (prefix.match(/["“”]/g) || []).length;
  if (totalQuotes % 2 !== 0) return null;

  const normalizedLabel = `${prefix}:`;

  // 1. Positive statement/option prefixes:
  // (Đúng), (Có), (Có thể), (Tích cực), (Không thiên kiến), (True), (Yes)
  if (
    /\(\s*(?:Đúng|Có|Có thể|Tích cực|Không thiên kiến|True|Yes)\s*\)\s*$/i.test(prefix) ||
    /(?:→|➔|—|-)\s*(?:Đúng|Có|Có thể|Tích cực|Không thiên kiến|True|Yes)(?:\s*\([^)]+\))?\s*$/i.test(prefix) ||
    /^(?:Hai|Ba|Các|Cả\s*\d*)\s+(?:lựa chọn|phương án|nhận định|mệnh đề|câu lệnh)(?:\s+đều)?\s+đúng$/i.test(prefix)
  ) {
    return {
      label: normalizedLabel,
      body: rest,
      tone: 'correct',
      className: 'text-emerald-700 font-semibold',
    };
  }

  // 2. Negative statement/option prefixes:
  // (Sai), (Không), (Không thể), (Tiêu cực), (Thiên kiến), (False), (No)
  if (
    /\(\s*(?:Sai|Không|Không thể|Tiêu cực|Thiên kiến|False|No)\s*\)\s*$/i.test(prefix) ||
    /(?:→|➔|—|-)\s*(?:Sai|Không|Không thể|Tiêu cực|Thiên kiến|False|No)(?:\s*\([^)]+\))?\s*$/i.test(prefix) ||
    /^(?:Hai|Ba|Các|Cả\s*\d*)\s+(?:lựa chọn|phương án|nhận định|mệnh đề|câu lệnh)(?:\s+còn lại)?(?:\s+đều)?\s+sai$/i.test(prefix)
  ) {
    return {
      label: normalizedLabel,
      body: rest,
      tone: 'wrong',
      className: 'text-rose-700 font-semibold',
    };
  }

  // 3. Quoted option prefixes (“...” : ... or "..." : ...) or <Label>: <Content> prefixes (<= 80 chars before :)
  const isQuotedPrefix = /^[“"'].+[”"']$/.test(prefix);
  const isExplicitStructuredPrefix =
    /^(?:Mệnh đề|Nhận định|Tùy chọn|Phương án|Lựa chọn|Câu lệnh|Ý|Vị trí|Bước|Cặp|Ô trống|Mục)\s*(?:\d+|[A-Z])?\b/i.test(prefix) ||
    prefix.includes('→') ||
    prefix.includes('➔');
  if (
    isQuotedPrefix ||
    ((prefix.length <= 80 || isExplicitStructuredPrefix) &&
      (prefix.includes('→') || prefix.includes('➔') || !prefix.includes('. ')))
  ) {
    if (!/\b(ví dụ|chẳng hạn|như sau|bao gồm|cụ thể là|nghĩa là|tốt nhất là|sau đây)\s*$/i.test(prefix)) {
      return {
        label: normalizedLabel,
        body: rest,
        tone: 'neutral',
        className: 'font-semibold text-slate-800',
      };
    }
  }

  return null;
};

const renderExplanationBulletRow = (content, key, allowLabelSplit = true, defaultDotTone = '', asListItem = false) => {
  const split = allowLabelSplit ? splitValidBulletLabel(content) : null;
  const dotTone = split?.tone === 'correct'
    ? 'dot-correct'
    : split?.tone === 'wrong'
    ? 'dot-wrong'
    : defaultDotTone;
  const labelColorStyle =
    split?.tone === 'correct'
      ? { color: '#047857' }
      : split?.tone === 'wrong'
      ? { color: '#be123c' }
      : split
      ? { color: '#1e293b' }
      : undefined;

  const RowTag = asListItem ? 'li' : 'div';

  return (
    <RowTag key={key} className="explanation-bullet-row">
      <span className={`explanation-bullet-dot${dotTone ? ` ${dotTone}` : ''}`}>•</span>
      <span className="explanation-bullet-text">
        {split ? (
          <>
            <strong
              className={`explanation-bullet-key ${split.className}`}
              style={labelColorStyle}
            >
              {split.label}
            </strong>{' '}
            {renderInlineMarkdown(split.body)}
          </>
        ) : (
          renderInlineMarkdown(content)
        )}
      </span>
    </RowTag>
  );
};

const renderCleanExplanation = (text, question = null) => {
  if (!text) return null;
  const parsed = parseStructuredExplanation(text, question);
  if (!parsed) return null;

  if (parsed.isStructured) {
    const introLines = parsed.intro
      ? parsed.intro.split('\n').map((l) => l.trim()).filter(Boolean)
      : [];
    const noteLines = parsed.note
      ? parsed.note.split('\n').map((l) => l.trim()).filter(Boolean)
      : [];

    return (
      <div className="explanation-structured-container">
        {introLines.map((line, iIdx) =>
          /^[•\-*]\s*/.test(line) && !/[:：]$/.test(line) && introLines.length > 1 ? (
            renderExplanationBulletRow(line.replace(/^[•\-*]\s*/, ''), `intro-${iIdx}`, true)
          ) : /[:：]$/.test(line) ? (
            <div key={`intro-${iIdx}`} className="explanation-subheading font-semibold text-slate-800 mt-2 mb-1">
              {renderInlineMarkdown(line.replace(/^[•\-*]\s*/, ''))}
            </div>
          ) : (
            <p key={`intro-${iIdx}`} className="explanation-prose mb-2 text-slate-700 leading-relaxed">
              {renderInlineMarkdown(line.replace(/^[•\-*]\s*/, ''))}
            </p>
          )
        )}

        {parsed.sections.map((sec, sIdx) => (
          <div key={sIdx} className={`explanation-section-card section-${sec.type}`}>
            <div className="explanation-section-header">
              <span className={`explanation-section-badge badge-${sec.type}`}>
                {sec.type === 'correct' ? `✓ ${sec.label}` : `✕ ${sec.label}`}
              </span>
            </div>
            <ul className="explanation-clean-list explanation-section-items">
              {sec.bullets.map((bullet, bIdx) =>
                renderExplanationBulletRow(bullet, bIdx, true, `dot-${sec.type}`, true)
              )}
            </ul>
          </div>
        ))}

        {noteLines.length > 0 && (
          <div className="explanation-callout-note">
            <div className="callout-note-icon">💡</div>
            <div className="callout-note-body">
              <strong className="callout-note-title">Giải thích thêm:</strong>
              <div className="callout-note-text">
                {noteLines.map((line, nIdx) =>
                  /^[•\-*]\s*/.test(line) && !/[:：]$/.test(line) && noteLines.length > 1 ? (
                    renderExplanationBulletRow(line.replace(/^[•\-*]\s*/, ''), `note-${nIdx}`, true)
                  ) : (
                    <p key={`note-${nIdx}`} className="explanation-prose mb-2 text-slate-700 leading-relaxed">
                      {renderInlineMarkdown(line.replace(/^[•\-*]\s*/, ''))}
                    </p>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Group contiguous bullet items into <ul className="explanation-clean-list">
  const blocks = [];
  let currentBulletGroup = [];

  const flushBullets = (keySuffix) => {
    if (currentBulletGroup.length > 0) {
      const bulletsToRender = currentBulletGroup;
      currentBulletGroup = [];
      blocks.push(
        <ul key={`list-${keySuffix}`} className="explanation-clean-list">
          {bulletsToRender.map((bItem, bIdx) =>
            renderExplanationBulletRow(
              bItem.content,
              `b-${keySuffix}-${bIdx}`,
              bItem.group !== 'answer',
              '',
              true
            )
          )}
        </ul>
      );
    }
  };

  parsed.items.forEach((item, idx) => {
    if (item.type === 'bullet') {
      currentBulletGroup.push(item);
      return;
    }
    flushBullets(idx);
    if (item.type === 'subheading') {
      blocks.push(
        <div key={`sub-${idx}`} className="explanation-subheading font-semibold text-slate-800 mt-2 mb-1">
          {renderInlineMarkdown(item.content)}
        </div>
      );
    } else {
      blocks.push(
        <p key={`prose-${idx}`} className="explanation-prose mb-2 text-slate-700 leading-relaxed">
          {renderInlineMarkdown(item.content)}
        </p>
      );
    }
  });
  flushBullets('final');

  return (
    <div className="explanation-structured">
      {blocks}
    </div>
  );
};

const renderStudyHint = (hintText, question = null) => {
  if (!hintText) return null;
  return (
    <div className="study-hint-content">
      {renderCleanExplanation(hintText, question)}
    </div>
  );
};

const MistakeCorrectionPanel = ({ question, answer, result }) => {
  if (!result) return null;
  const isCorrect = Boolean(result.is_correct);
  const itype = getEffectiveInteractionType(question);
  const correctAns = result.correct_answer || {};

  // Resolve user answer display
  const renderUserAnswer = () => {
    if (itype === 'single_choice' || itype === 'multi_select') {
      const choices = question?.answer_model?.choices || [];
      const selectedIds = answer?.selected_choice_ids || [];
      if (!selectedIds.length) return <em className="empty-val">(Chưa chọn)</em>;
      const selectedChoices = choices
        .map((c, idx) => ({ ...c, letter: String.fromCharCode(65 + idx) }))
        .filter((c) => selectedIds.includes(c.id));
      return (
        <ul className="correction-list">
          {selectedChoices.map((c) => (
            <li key={c.id || c.letter} className={isCorrect ? 'text-correct' : 'text-wrong'}>
              <strong>{c.letter}.</strong> {c.text}
            </li>
          ))}
        </ul>
      );
    }
    if (itype === 'choice_matrix') {
      const rows = question?.answer_model?.rows || [];
      const userRows = answer?.rows || {};
      const corrRows = correctAns?.rows || {};
      return (
        <div className="correction-matrix-rows">
          {rows.map((r, idx) => {
            const userVal = userRows[r.id];
            const corrVal = corrRows[r.id] || r.correct || r.correct_value;
            const isRowCorrect = userVal !== undefined && Boolean(corrVal) && isSameMatrixOption(userVal, corrVal);
            return (
              <div key={r.id || idx} className={`correction-matrix-item ${isRowCorrect ? 'row-correct' : 'row-wrong'}`}>
                <span className="statement-num">{idx + 1}.</span>
                <span className="statement-text">{r.statement}</span>
                <span className="badge-arrow">→</span>
                <span className={`badge-answer ${isRowCorrect ? 'correct' : 'wrong'}`}>
                  {userVal ? getBinaryDisplayLabel(userVal, r.options) : '(Chưa chọn)'}
                </span>
                {!isRowCorrect && corrVal && (
                  <span className="badge-answer correct inline-correction-pill">
                    → Đáp án đúng: {getBinaryDisplayLabel(corrVal, r.options)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    if (itype === 'drag_drop_classification') {
      const mapping = answer?.mapping || {};
      const corrMapping = correctAns?.mapping || question?.answer_model?.correct_mapping || {};
      const items = question?.answer_model?.items || [];
      return (
        <div className="correction-mapping-rows">
          {items.map((it, idx) => {
            const itText = typeof it === 'string' ? it : it.text;
            const userZone = mapping[itText];
            const corrZone = corrMapping[itText];
            const isItemCorrect = Boolean(userZone && corrZone && String(userZone).trim() === String(corrZone).trim());
            return (
              <div key={idx} className={`correction-mapping-item ${isItemCorrect ? 'row-correct' : 'row-wrong'}`}>
                <span className="mapping-item-name">{itText}</span>
                <span className="badge-arrow">→</span>
                <span className={`badge-answer ${isItemCorrect ? 'correct' : 'wrong'}`}>
                  {userZone || '(Chưa ghép)'}
                </span>
                {!isItemCorrect && corrZone && (
                  <span className="badge-answer correct inline-correction-pill">
                    → Đáp án đúng: {corrZone}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    if (itype === 'dropdown_matrix') {
      const rows = question?.answer_model?.rows || [];
      const userRows = answer?.rows || {};
      const corrRows = correctAns?.rows || {};
      return (
        <div className="correction-dropdown-rows">
          {rows.map((r, idx) => {
            const userFieldVals = userRows[r.id] || {};
            const corrFieldVals = corrRows[r.id] || r.correct || {};
            return (
              <div key={r.id || idx} className="correction-dropdown-item">
                <div className="statement-title">{idx + 1}. {r.statement}</div>
                {r.fields?.map((f) => {
                  const uVal = userFieldVals[f.label] || userFieldVals[f.id];
                  const cVal = corrFieldVals[f.label] || corrFieldVals[f.id];
                  const isFCorrect = Boolean(uVal && cVal && String(uVal).trim() === String(cVal).trim());
                  return (
                    <div key={f.id || f.label} className={`field-row ${isFCorrect ? 'row-correct' : 'row-wrong'}`}>
                      <span className="field-name">{f.label}:</span>
                      <span className={`badge-answer ${isFCorrect ? 'correct' : 'wrong'}`}>{uVal || '(Chưa chọn)'}</span>
                      {!isFCorrect && cVal && (
                        <span className="badge-answer correct inline-correction-pill">
                          → Đáp án đúng: {cVal}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    }
    if (itype === 'sequence') {
      const corrOrder = correctAns?.order || question?.answer_model?.correct_order || [];
      if (!answer?.order?.length) {
        return (
          <div className="correction-matrix-rows">
            <div className="correction-matrix-item row-wrong">
              <span className="badge-answer wrong">Chưa sắp xếp (Bỏ trống)</span>
            </div>
          </div>
        );
      }
      const userOrder = answer.order;
      return (
        <div className="correction-matrix-rows">
          {userOrder.map((step, idx) => {
            const corrStep = corrOrder[idx];
            const isStepCorrect = Boolean(corrStep && step && String(step).trim() === String(corrStep).trim());
            return (
              <div key={idx} className={`correction-matrix-item ${isStepCorrect ? 'row-correct' : 'row-wrong'}`}>
                <span className="statement-num">Bước {idx + 1}.</span>
                <span className="statement-text">{step}</span>
                <span className={`badge-answer ${isStepCorrect ? 'correct' : 'wrong'}`}>
                  {isStepCorrect ? '✓ Đúng vị trí' : '✕ Sai vị trí'}
                </span>
                {!isStepCorrect && corrStep && (
                  <span className="badge-answer correct inline-correction-pill">
                    → Đáp án đúng: {corrStep}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Resolve correct answer display
  const renderCorrectAnswer = () => {
    if (itype === 'single_choice' || itype === 'multi_select') {
      const choices = question?.answer_model?.choices || [];
      const corrIds = correctAns?.correct_choice_ids || question?.answer_model?.correct_choice_ids || [];
      const correctChoices = choices
        .map((c, idx) => ({ ...c, letter: String.fromCharCode(65 + idx) }))
        .filter((c) => corrIds.includes(c.id));
      return (
        <ul className="correction-list correct-list">
          {correctChoices.map((c) => (
            <li key={c.id || c.letter} className="text-correct">
              <strong>{c.letter}.</strong> {c.text}
            </li>
          ))}
        </ul>
      );
    }
    if (itype === 'choice_matrix') {
      const rows = question?.answer_model?.rows || [];
      const corrRows = correctAns?.rows || {};
      return (
        <div className="correction-matrix-rows correct">
          {rows.map((r, idx) => {
            const corrVal = corrRows[r.id] || r.correct || r.correct_value;
            return (
              <div key={r.id || idx} className="correction-matrix-item row-correct">
                <span className="statement-num">{idx + 1}.</span>
                <span className="statement-text">{r.statement}</span>
                <span className="badge-arrow">→</span>
                <span className="badge-answer correct">{getBinaryDisplayLabel(corrVal, r.options)}</span>
              </div>
            );
          })}
        </div>
      );
    }
    if (itype === 'drag_drop_classification') {
      const corrMapping = correctAns?.mapping || question?.answer_model?.correct_mapping || {};
      const items = question?.answer_model?.items || [];
      return (
        <div className="correction-mapping-rows correct">
          {items.map((it, idx) => {
            const itText = typeof it === 'string' ? it : it.text;
            const corrZone = corrMapping[itText];
            return (
              <div key={idx} className="correction-mapping-item row-correct">
                <span className="mapping-item-name">{itText}</span>
                <span className="badge-arrow">→</span>
                <span className="badge-answer correct">{corrZone}</span>
              </div>
            );
          })}
        </div>
      );
    }
    if (itype === 'dropdown_matrix') {
      const rows = question?.answer_model?.rows || [];
      const corrRows = correctAns?.rows || {};
      return (
        <div className="correction-dropdown-rows correct">
          {rows.map((r, idx) => {
            const corrFieldVals = corrRows[r.id] || r.correct || {};
            return (
              <div key={r.id || idx} className="correction-dropdown-item">
                <div className="statement-title">{idx + 1}. {r.statement}</div>
                {r.fields?.map((f) => {
                  const cVal = corrFieldVals[f.label] || corrFieldVals[f.id];
                  return (
                    <div key={f.id || f.label} className="field-row row-correct">
                      <span className="field-name">{f.label}:</span>
                      <span className="badge-answer correct">{cVal}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    }
    if (itype === 'sequence') {
      const corrOrder = correctAns?.order || question?.answer_model?.correct_order || [];
      return (
        <ol className="correction-sequence-list correct">
          {corrOrder.map((step, idx) => (
            <li key={idx} className="text-correct">
              {step}
            </li>
          ))}
        </ol>
      );
    }
    return null;
  };

  const rawFeedback = question?.feedback || question?.prompt?.feedback || result.feedback || '';
  const strippedFeedback = /(?:^|\n)\s*Đáp án(?:\s+đúng)?\s*[:：]/i.test(rawFeedback) && /\n\s*Giải thích(?:\s+chi\s+tiết)?\s*[:：]/i.test(rawFeedback)
    ? rawFeedback.replace(/^[\s\S]*?\n\s*Giải thích(?:\s+chi\s+tiết)?\s*[:：]\s*\n?/i, '')
    : rawFeedback;
  const explanation = cleanExplanationText(strippedFeedback, question);

  return (
    <div className={`mistake-correction-card ${isCorrect ? 'is-correct' : 'is-wrong'}`}>
      <div className="correction-header">
        <span className={`correction-badge ${isCorrect ? 'badge-success' : 'badge-danger'}`}>
          {isCorrect ? '✓ Chính xác' : '✕ Chưa chính xác'}
        </span>
      </div>

      <div className={`correction-section section-user-answer ${isCorrect ? 'user-answer-correct' : 'user-answer-wrong'}`}>
        <div className="correction-section-title">Câu trả lời của bạn:</div>
        <div className="correction-section-body">{renderUserAnswer()}</div>
      </div>

      <div className="correction-section section-correct-answer">
        <div className="correction-section-title">Đáp án đúng:</div>
        <div className="correction-section-body">{renderCorrectAnswer()}</div>
      </div>

      {explanation && (
        <div className="correction-section section-explanation">
          <div className="correction-section-title">Giải thích chi tiết:</div>
          <div className="correction-section-body explanation-text">
            {renderCleanExplanation(explanation, question)}
          </div>
        </div>
      )}
    </div>
  );
};

const MatchingRenderer = ({ question, answer, onAnswerChange, disabled, result = null }) => {
  const items = useMemo(() => question.answer_model.items || [], [question.answer_model.items]);
  const zones = useMemo(() => question.answer_model.zones || [], [question.answer_model.zones]);
  const mapping = useMemo(() => {
    const raw = answer.mapping || {};
    const validItems = new Set(items);
    const validZones = new Set(zones);
    return Object.fromEntries(
      Object.entries(raw).filter(([k, v]) => validItems.has(k) && validZones.has(v))
    );
  }, [answer.mapping, items, zones]);
  const [activeItem, setActiveItem] = useState(null);
  const [lines, setLines] = useState([]);
  const [dragState, setDragState] = useState(null);
  const boardRef = useRef(null);
  const itemRefs = useRef({});
  const zoneRefs = useRef({});
  const reviewMode = Boolean(result);
  const correctMapping = useMemo(() => (
    result?.correct_answer?.mapping || question.answer_model.correct_mapping || {}
  ), [question.answer_model.correct_mapping, result]);

  const connect = useCallback((item, zone) => {
    if (!item || !zone || disabled || !items.includes(item) || !zones.includes(zone)) return;
    const next = { ...mapping };
    if (next[item] === zone) delete next[item];
    else next[item] = zone;
    onAnswerChange({ mapping: next });
    setActiveItem(null);
  }, [disabled, items, mapping, onAnswerChange, zones]);

  const beginConnection = (event, item) => {
    if (disabled || !boardRef.current || !itemRefs.current[item]) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const boardRect = boardRef.current.getBoundingClientRect();
    const itemRect = itemRefs.current[item].getBoundingClientRect();
    setDragState({
      item,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      x1: itemRect.right - boardRect.left,
      y1: itemRect.top + itemRect.height / 2 - boardRect.top,
      x2: event.clientX - boardRect.left,
      y2: event.clientY - boardRect.top,
      moved: false,
    });
  };

  const moveConnection = (event) => {
    setDragState((current) => {
      if (!current || current.pointerId !== event.pointerId || !boardRef.current) return current;
      const boardRect = boardRef.current.getBoundingClientRect();
      const moved = current.moved
        || Math.hypot(event.clientX - current.startClientX, event.clientY - current.startClientY) > 4;
      if (moved) setActiveItem(current.item);
      return { ...current, x2: event.clientX - boardRect.left, y2: event.clientY - boardRect.top, moved };
    });
  };

  const finishConnection = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    if (dragState.moved) {
      const target = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-match-zone]');
      if (target?.dataset.matchZone) connect(dragState.item, target.dataset.matchZone);
      else setActiveItem(dragState.item);
    } else {
      setActiveItem((current) => current === dragState.item ? null : dragState.item);
    }
    setDragState(null);
  };

  useLayoutEffect(() => {
    const measure = () => {
      const board = boardRef.current;
      if (!board) return;
      const boardRect = board.getBoundingClientRect();
      const linePairs = reviewMode
        ? [
            ...Object.entries(mapping)
              .filter(([item, zone]) => correctMapping[item] && correctMapping[item] !== zone)
              .map(([item, zone]) => ({ item, zone, status: 'wrong', key: `wrong-${item}` })),
            ...Object.entries(correctMapping)
              .map(([item, zone]) => ({ item, zone, status: 'correct', key: `correct-${item}` })),
          ]
        : Object.entries(mapping).map(([item, zone]) => ({ item, zone, status: 'current', key: item }));

      const nextLines = linePairs.flatMap(({ item, zone, status, key }) => {
        const itemNode = itemRefs.current[item];
        const zoneNode = zoneRefs.current[zone];
        if (!itemNode || !zoneNode) return [];
        const from = itemNode.getBoundingClientRect();
        const to = zoneNode.getBoundingClientRect();
        return [{
          item,
          key,
          status,
          x1: from.right - boardRect.left,
          y1: from.top + from.height / 2 - boardRect.top,
          x2: to.left - boardRect.left,
          y2: to.top + to.height / 2 - boardRect.top,
        }];
      });
      setLines(nextLines);
    };
    const frame = window.requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    if (boardRef.current) observer.observe(boardRef.current);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [mapping, correctMapping, items, zones, reviewMode]);

  const connectedZones = new Set(reviewMode
    ? [...Object.values(mapping), ...Object.values(correctMapping)]
    : Object.values(mapping));
  const isWrongItem = (item) => reviewMode && mapping[item] && correctMapping[item] && mapping[item] !== correctMapping[item];
  const isCorrectItem = (item) => reviewMode && correctMapping[item] && mapping[item] === correctMapping[item];
  const isCorrectZone = (zone) => reviewMode && Object.values(correctMapping).includes(zone);
  const isWrongZone = (zone) => reviewMode && Object.entries(mapping).some(([item, mappedZone]) => mappedZone === zone && correctMapping[item] && correctMapping[item] !== zone);

  return (
    <div className="matching-experience">
      <div className="matching-hint"><InformationCircleIcon /><span>Chọn một thẻ ở cột trái, sau đó chọn nội dung tương ứng ở cột phải. Bạn cũng có thể kéo và thả.</span></div>
      <div className={`matching-board ${dragState?.moved ? 'is-connecting' : ''}`} onPointerCancel={finishConnection} onPointerMove={moveConnection} onPointerUp={finishConnection} ref={boardRef}>
        <svg className="matching-lines" aria-hidden="true">
          {lines.map((line) => {
            const bend = Math.max(42, (line.x2 - line.x1) * 0.42);
            return <path className={`matching-line ${line.status}`} d={`M ${line.x1} ${line.y1} C ${line.x1 + bend} ${line.y1}, ${line.x2 - bend} ${line.y2}, ${line.x2} ${line.y2}`} key={line.key} />;
          })}
          {dragState?.moved && <path className="live-connection" d={`M ${dragState.x1} ${dragState.y1} C ${dragState.x1 + Math.max(42, (dragState.x2 - dragState.x1) * .42)} ${dragState.y1}, ${dragState.x2 - Math.max(42, (dragState.x2 - dragState.x1) * .42)} ${dragState.y2}, ${dragState.x2} ${dragState.y2}`} />}
        </svg>
        <div className="matching-column matching-source-column">
          <div className="matching-column-title"><span>Thẻ cần ghép</span><small>{items.length} nội dung</small></div>
          <div className="matching-list">
            {items.map((item) => {
              const connected = Boolean(mapping[item]);
              const active = activeItem === item;
              const reviewClass = isWrongItem(item) ? 'review-wrong' : isCorrectItem(item) ? 'review-correct' : '';
              return (
                <button
                  aria-pressed={active}
                  className={`matching-card source ${connected ? 'connected' : ''} ${active ? 'selected' : ''} ${reviewClass}`}
                  disabled={disabled}
                  key={item}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setActiveItem(active ? null : item);
                    }
                  }}
                  onPointerDown={(event) => beginConnection(event, item)}
                  ref={(node) => { itemRefs.current[item] = node; }}
                  type="button"
                >
                  <span className="matching-card-copy">{item}{connected && <small>{isWrongItem(item) ? 'Cần sửa' : 'Đã nối'}</small>}</span>
                  <span className="matching-port" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </div>

        <div className="matching-column matching-target-column">
          <div className="matching-column-title"><span>Nội dung tương ứng</span><small>{zones.length} lựa chọn</small></div>
          <div className="matching-list">
            {zones.map((zone, index) => {
              const connected = connectedZones.has(zone);
              const reviewClass = isWrongZone(zone) ? 'review-wrong' : isCorrectZone(zone) ? 'review-correct' : '';
              return (
                <button
                  className={`matching-card target ${connected ? 'connected' : ''} ${activeItem ? 'available' : ''} ${reviewClass}`}
                  data-match-zone={zone}
                  disabled={disabled}
                  key={zone}
                  onClick={() => activeItem && connect(activeItem, zone)}
                  ref={(node) => { zoneRefs.current[zone] = node; }}
                  type="button"
                >
                  <span className="matching-port" aria-hidden="true" />
                  <span className="matching-index">{index + 1}</span>
                  <span className="matching-card-copy">{zone}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="matching-footer">
        <button className="clear-matches" disabled={disabled || Object.keys(mapping).length === 0} onClick={() => { onAnswerChange({ mapping: {} }); setActiveItem(null); }} type="button"><TrashIcon /> Xóa tất cả kết nối</button>
        <span><LinkIcon /> {reviewMode ? 'Đáp án đúng' : 'Đã nối'} <strong>{reviewMode ? `${Object.keys(correctMapping).length}/${items.length}` : `${Object.keys(mapping).length}/${items.length}`}</strong> cặp</span>
      </div>
    </div>
  );
};

const ClassificationDropRenderer = ({ question, answer, onAnswerChange, disabled, result = null }) => {
  const items = useMemo(() => question.answer_model.items || [], [question.answer_model.items]);
  const zones = useMemo(() => question.answer_model.zones || [], [question.answer_model.zones]);
  const mapping = useMemo(() => {
    const raw = answer.mapping || {};
    const validItems = new Set(items);
    const validZones = new Set(zones);
    return Object.fromEntries(
      Object.entries(raw).filter(([k, v]) => validItems.has(k) && validZones.has(v))
    );
  }, [answer.mapping, items, zones]);
  const [activeItem, setActiveItem] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const reviewMode = Boolean(result);
  const correctMapping = useMemo(() => (
    result?.correct_answer?.mapping || question.answer_model.correct_mapping || {}
  ), [question.answer_model.correct_mapping, result]);

  const assignItem = useCallback((item, zone) => {
    if (!item || !zone || disabled || !items.includes(item) || !zones.includes(zone)) return;
    onAnswerChange({ mapping: { ...mapping, [item]: zone } });
    setActiveItem(null);
    setDragItem(null);
  }, [disabled, items, mapping, onAnswerChange, zones]);

  const removeItem = useCallback((item) => {
    if (!item || disabled) return;
    const next = { ...mapping };
    delete next[item];
    onAnswerChange({ mapping: next });
  }, [disabled, mapping, onAnswerChange]);

  const itemsByZone = useMemo(() => zones.reduce((acc, zone) => {
    acc[zone] = items.filter((item) => mapping[item] === zone);
    return acc;
  }, {}), [items, mapping, zones]);

  const unassignedItems = items.filter((item) => !mapping[item]);
  const displayUnassigned = reviewMode
    ? items.filter((item) => !correctMapping[item])
    : unassignedItems;
  const answeredCount = Object.keys(mapping).length;
  const userCorrectCount = reviewMode
    ? items.filter((item) => mapping[item] && correctMapping[item] === mapping[item]).length
    : 0;
  const userWrongCount = reviewMode
    ? items.filter((item) => mapping[item] && correctMapping[item] && correctMapping[item] !== mapping[item]).length
    : 0;

  const getItemReviewClass = (item, zone) => {
    if (!reviewMode) return '';
    if (correctMapping[item] === zone) return 'review-correct';
    if (mapping[item] === zone && correctMapping[item] !== zone) return 'review-wrong';
    return '';
  };

  const zoneReviewItems = (zone) => {
    return itemsByZone[zone] || [];
  };

  const getZoneReviewStats = (zone, zoneItems) => {
    if (!reviewMode) return { correct: 0, wrong: 0 };
    return zoneItems.reduce((stats, item) => {
      const status = getItemReviewClass(item, zone);
      if (status === 'review-correct') return { ...stats, correct: stats.correct + 1 };
      if (status === 'review-wrong') return { ...stats, wrong: stats.wrong + 1 };
      return stats;
    }, { correct: 0, wrong: 0 });
  };

  return (
    <div className="classification-experience">
      <div className="matching-hint">
        <InformationCircleIcon />
        <span>Kéo từng thẻ vào vùng phân loại phù hợp. Bạn cũng có thể bấm chọn thẻ rồi bấm vào vùng cần thả.</span>
      </div>

      <section className="classification-tray" aria-label="Thẻ chưa phân loại">
        <div className="classification-heading">
          <span>Thẻ cần phân loại</span>
          <small>{displayUnassigned.length} thẻ</small>
        </div>
        <div className="classification-chip-list">
          {displayUnassigned.length === 0 ? (
            <div className="classification-empty">Tất cả thẻ đã được đưa vào vùng phân loại.</div>
          ) : displayUnassigned.map((item) => (
            <button
              className={`classification-chip ${activeItem === item ? 'selected' : ''}`}
              disabled={disabled || reviewMode}
              draggable={!disabled && !reviewMode}
              key={item}
              onClick={() => setActiveItem((current) => current === item ? null : item)}
              onDragEnd={() => setDragItem(null)}
              onDragStart={(event) => {
                setDragItem(item);
                event.dataTransfer.setData('application/x-saoviet-chip', item);
                event.dataTransfer.setData('text/plain', item);
                event.dataTransfer.effectAllowed = 'move';
              }}
              type="button"
            >
              {item}
            </button>
          ))}
        </div>
      </section>

      <div className="classification-zones">
        {zones.map((zone, index) => {
          const zoneItems = zoneReviewItems(zone);
          const isActiveDrop = Boolean(activeItem || dragItem);
          const zoneStats = getZoneReviewStats(zone, zoneItems);
          const zoneReviewClass = reviewMode
            ? zoneStats.wrong > 0 ? 'has-wrong' : 'all-correct'
            : '';
          return (
            <section
              className={`classification-zone ${isActiveDrop ? 'ready' : ''} ${zoneReviewClass}`}
              key={zone}
              onClick={() => activeItem && assignItem(activeItem, zone)}
              onDragOver={(event) => {
                if (disabled || reviewMode) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(event) => {
                event.preventDefault();
                const item = event.dataTransfer.getData('application/x-saoviet-chip') || dragItem || event.dataTransfer.getData('text/plain');
                if (item && items.includes(item)) {
                  assignItem(item, zone);
                }
              }}
            >
              <header>
                <span>{index + 1}</span>
                <strong>{zone}</strong>
                <small>{reviewMode ? `${zoneStats.correct} đúng · ${zoneStats.wrong} sai` : `${zoneItems.length} thẻ`}</small>
              </header>
              <div className="classification-zone-body">
                {zoneItems.length === 0 ? (
                  <div className="classification-placeholder">Thả thẻ vào đây</div>
                ) : zoneItems.map((item) => {
                  const reviewClass = getItemReviewClass(item, zone);
                  const correctZone = correctMapping[item];
                  const isCorrect = reviewClass === 'review-correct';
                  const isWrong = reviewClass === 'review-wrong';
                  return (
                    <div className={`classification-assigned ${reviewClass}`} key={`${zone}-${item}`}>
                      <span>{item}</span>
                      {reviewMode ? (
                        <b className="classification-result-badge">
                          {isCorrect ? <CheckCircleIcon /> : isWrong ? <XCircleIcon /> : null}
                          {isCorrect ? 'Đúng' : isWrong ? 'Bạn đặt sai' : 'Chưa chọn'}
                        </b>
                      ) : null}
                      {reviewMode && isWrong && correctZone ? <small>Đáp án đúng: {correctZone}</small> : null}
                      {!disabled && !reviewMode ? (
                        <button aria-label={`Bỏ phân loại ${item}`} onClick={(event) => { event.stopPropagation(); removeItem(item); }} type="button">
                          <XCircleIcon />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <div className={`classification-footer ${reviewMode && userWrongCount > 0 ? 'has-wrong' : reviewMode ? 'all-correct' : ''}`}>
        <button className="clear-matches" disabled={disabled || Object.keys(mapping).length === 0} onClick={() => { onAnswerChange({ mapping: {} }); setActiveItem(null); }} type="button"><TrashIcon /> Xóa tất cả phân loại</button>
        <span>
          {reviewMode && userWrongCount > 0 ? <XCircleIcon /> : <LinkIcon />}
          {reviewMode ? 'Kết quả của bạn' : 'Đã phân loại'}
          <strong>{reviewMode ? `${userCorrectCount}/${items.length}` : `${answeredCount}/${items.length}`}</strong>
          thẻ
        </span>
      </div>
    </div>
  );
};

const SequenceRenderer = ({ question, answer, onAnswerChange, disabled, result = null }) => {
  const defaultSteps = question.answer_model?.steps || [];
  const validStepSet = new Set(defaultSteps);
  const rawUserOrder = Array.isArray(answer.order)
    ? answer.order.filter((step) => typeof step === 'string' && validStepSet.has(step))
    : [];
  const hasUserOrder = rawUserOrder.length === defaultSteps.length && defaultSteps.length > 0;
  const order = hasUserOrder ? rawUserOrder : defaultSteps;
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const touched = Boolean(answer.sequence_touched) || (hasUserOrder && order.some((step, index) => step !== defaultSteps[index]));
  const reviewMode = Boolean(result);
  const correctOrder = result?.correct_answer?.order || question.answer_model?.correct_order || [];

  const updateOrder = (next) => onAnswerChange({ order: next, sequence_touched: true });

  const move = (fromIndex, toIndex) => {
    if (
      disabled
      || !Number.isInteger(fromIndex)
      || !Number.isInteger(toIndex)
      || fromIndex < 0
      || fromIndex >= order.length
      || toIndex < 0
      || toIndex >= order.length
      || fromIndex === toIndex
    ) {
      return;
    }
    const next = [...order];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    updateOrder(next);
  };

  return (
    <div className="sequence-experience">
      <div className="sequence-hint">
        <InformationCircleIcon />
        <span>Kéo thả từng bước để đổi thứ tự, hoặc dùng nút lên/xuống ở cuối mỗi dòng.</span>
      </div>
      <div className="sequence-list">
        {order.map((step, index) => {
          const expectedStep = correctOrder[index];
          const isStepCorrect = reviewMode && hasUserOrder && Boolean(expectedStep) && String(step).trim() === String(expectedStep).trim();
          const isStepWrong = reviewMode && Boolean(expectedStep) && (!hasUserOrder || String(step).trim() !== String(expectedStep).trim());

          return (
            <div
              className={[
                'sequence-row',
                dragIndex === index ? 'dragging' : '',
                dropIndex === index && dragIndex !== index ? 'drop-target' : '',
                isStepCorrect ? 'row-correct' : '',
                isStepWrong ? 'row-wrong' : '',
              ].filter(Boolean).join(' ')}
              draggable={!disabled}
              key={`${step}_${index}`}
              onDragEnd={() => {
                setDragIndex(null);
                setDropIndex(null);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) setDropIndex(index);
              }}
              onDragStart={(event) => {
                setDragIndex(index);
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('application/x-saoviet-seq-index', String(index));
                event.dataTransfer.setData('text/plain', String(index));
              }}
              onDrop={(event) => {
                event.preventDefault();
                const rawFrom = event.dataTransfer.getData('application/x-saoviet-seq-index') || (dragIndex !== null ? String(dragIndex) : '');
                const from = Number.parseInt(rawFrom, 10);
                if (Number.isInteger(from) && from >= 0 && from < order.length) {
                  move(from, index);
                }
                setDragIndex(null);
                setDropIndex(null);
              }}
            >
              <div className="sequence-grip" aria-hidden="true">
                <Bars3BottomLeftIcon />
              </div>
              <strong>{index + 1}</strong>
              <div className="sequence-step-content">
                <span>{step}</span>
                {reviewMode && isStepCorrect && (
                  <small className="sequence-review-badge correct">✓ Đúng vị trí</small>
                )}
                {reviewMode && isStepWrong && expectedStep && (
                  <small className="sequence-review-badge wrong">
                    → Đáp án đúng: {expectedStep}
                  </small>
                )}
              </div>
              <div className="sequence-actions">
                <button className="icon-btn" disabled={disabled || index === 0} onClick={() => move(index, index - 1)} title="Đưa lên trên" type="button">
                  <ArrowUpIcon />
                </button>
                <button className="icon-btn" disabled={disabled || index === order.length - 1} onClick={() => move(index, index + 1)} title="Đưa xuống dưới" type="button">
                  <ArrowDownIcon />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="sequence-footer">
        <span className={touched ? 'confirmed' : ''}>
          <CheckCircleIcon style={{ width: 17, height: 17, minWidth: 17, flexShrink: 0 }} />
          {touched ? 'Đã ghi nhận thứ tự hiện tại' : 'Chưa xác nhận thứ tự'}
        </span>
        {!disabled && (
          <button className="sequence-confirm" onClick={() => onAnswerChange({ order, sequence_touched: true })} type="button">
            Xác nhận thứ tự này
          </button>
        )}
      </div>
    </div>
  );
};

// Kept as a fallback for older matrix layouts; binary review uses BinaryMatrixReviewRenderer.
// eslint-disable-next-line no-unused-vars
const MatrixRenderer = ({ question, answer, onAnswerChange, disabled, result = null }) => {
  const selectedRows = answer.rows || {};
  const matrixRows = question.answer_model.rows || [];
  const columnOptions = matrixRows[0]?.options || [];
  const answeredRows = matrixRows.filter((row) => selectedRows[row.id] !== undefined).length;
  const gridStyle = { gridTemplateColumns: `minmax(280px, 1fr) repeat(${Math.max(1, columnOptions.length)}, minmax(120px, 0.28fr))` };
  const isPositive = (option) => /^(có|yes|đúng|true)$/i.test(option.trim());
  const isNegative = (option) => /^(không|no|sai|false)$/i.test(option.trim());

  return (
    <div className="matrix-experience">
      <div className="matrix-hint"><InformationCircleIcon /><span>Chọn một phương án phù hợp cho từng nhận định.</span></div>
      <div className="matrix-grid matrix-grid-header" style={gridStyle}>
        <strong>Nhận định</strong>
        {columnOptions.map((option) => <strong key={option}>{option}</strong>)}
      </div>
      <div className="matrix-grid-body">
        {matrixRows.map((row, rowIndex) => (
          <div className={`matrix-grid matrix-statement-row ${selectedRows[row.id] !== undefined ? 'answered' : ''}`} key={row.id} style={gridStyle}>
            <div className="matrix-statement"><span>{String(rowIndex + 1).padStart(2, '0')}</span><p>{row.statement}</p></div>
            {row.options.map((option) => {
              const selected = selectedRows[row.id] === option;
              return (
                <button
                  className={`matrix-choice ${selected ? 'selected' : ''} ${isPositive(option) ? 'positive' : ''} ${isNegative(option) ? 'negative' : ''}`}
                  disabled={disabled}
                  key={option}
                  onClick={() => onAnswerChange({ rows: { ...selectedRows, [row.id]: option } })}
                  type="button"
                >
                  {isNegative(option) ? <XCircleIcon /> : <CheckCircleIcon />}
                  <span>{option}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className={`matrix-completion ${answeredRows === matrixRows.length ? 'complete' : ''}`}><CheckCircleIcon style={{ width: 17, height: 17, minWidth: 17, flexShrink: 0 }} /><span>{answeredRows === matrixRows.length ? 'Đã trả lời đầy đủ' : 'Tiến độ trả lời'} <strong>{answeredRows}/{matrixRows.length}</strong> ý</span></div>
    </div>
  );
};

const BinaryMatrixReviewRenderer = ({ question, answer, onAnswerChange, disabled, result = null }) => {
  const selectedRows = answer.rows || {};
  const matrixRows = question.answer_model.rows || [];
  const columnOptions = matrixRows[0]?.options || [];
  const answeredRows = matrixRows.filter((row) => selectedRows[row.id] !== undefined).length;
  const correctRows = result?.correct_answer?.rows || Object.fromEntries(matrixRows.map((row) => [row.id, row.correct || row.correct_value]));
  const reviewMode = Boolean(result);
  const gridStyle = { gridTemplateColumns: `minmax(280px, 1fr) repeat(${Math.max(1, columnOptions.length)}, minmax(135px, 0.32fr))` };
  const colLabel0 = columnOptions[0] ? getBinaryDisplayLabel(columnOptions[0], columnOptions) : 'Đúng';
  const colLabel1 = columnOptions[1] ? getBinaryDisplayLabel(columnOptions[1], columnOptions) : 'Sai';

  return (
    <div className="matrix-experience">
      <div className="matrix-hint">
        <InformationCircleIcon />
        <span>
          {columnOptions.length === 2
            ? `Chọn ${colLabel0} hoặc ${colLabel1} cho từng nhận định dưới đây.`
            : 'Chọn phương án phù hợp cho từng nhận định dưới đây.'}
        </span>
      </div>
      <div className="matrix-grid matrix-grid-header" style={gridStyle}>
        <strong>Nhận định</strong>
        {columnOptions.map((option) => <strong key={option}>{getBinaryDisplayLabel(option, columnOptions)}</strong>)}
      </div>
      <div className="matrix-grid-body">
        {matrixRows.map((row, rowIndex) => {
          const selectedValue = selectedRows[row.id];
          const correctValue = correctRows[row.id];
          const rowCorrect = reviewMode
            && selectedValue !== undefined
            && Boolean(correctValue)
            && isSameMatrixOption(selectedValue, correctValue);
          const rowWrong = reviewMode && !rowCorrect;

          return (
            <div className={`matrix-grid matrix-statement-row ${selectedValue !== undefined ? 'answered' : ''} ${rowWrong ? 'review-wrong' : reviewMode ? 'review-correct' : ''}`} key={row.id} style={gridStyle}>
              <div className="matrix-statement"><span>{String(rowIndex + 1).padStart(2, '0')}</span><p>{row.statement}</p></div>
              {row.options.map((option) => {
                const selected = selectedValue === option;
                const correct = reviewMode && isSameMatrixOption(correctValue, option);
                const wrong = reviewMode && selected && !correct;
                const normOpt = normalizeMatrixOption(option);
                const isExplicitNegative = ['no', 'khong', 'sai', 'false', 'khong the', 'tieu cuc', 'thien kien'].includes(normOpt);
                const showXIcon = wrong || (!correct && isExplicitNegative);
                return (
                  <button
                    className={`matrix-choice ${selected ? 'selected' : ''} ${correct ? 'correct-answer' : ''} ${wrong ? 'wrong-answer' : ''}`}
                    disabled={disabled}
                    key={option}
                    onClick={() => onAnswerChange({ rows: { ...selectedRows, [row.id]: option } })}
                    type="button"
                  >
                    {showXIcon ? <XCircleIcon /> : <CheckCircleIcon />}
                    <span>{getBinaryDisplayLabel(option, row.options)}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className={`matrix-completion ${answeredRows === matrixRows.length ? 'complete' : ''}`}><CheckCircleIcon style={{ width: 17, height: 17, minWidth: 17, flexShrink: 0 }} /><span>{answeredRows === matrixRows.length ? 'Đã trả lời đầy đủ' : 'Tiến độ trả lời'} <strong>{answeredRows}/{matrixRows.length}</strong> ý</span></div>
    </div>
  );
};

const MatrixDropdownRenderer = ({ question, answer, onAnswerChange, disabled, result = null }) => {
  const selectedRows = answer.rows || {};
  const matrixRows = question.answer_model.rows || [];
  const answeredRows = matrixRows.filter((row) => Boolean(selectedRows[row.id])).length;
  const reviewMode = Boolean(result);
  const correctRows = result?.correct_answer?.rows || Object.fromEntries(
    matrixRows.map((row) => [row.id, row.correct || row.correct_value || ''])
  );

  return (
    <div className="matrix-experience matrix-dropdown-experience">
      <div className="matrix-hint">
        <InformationCircleIcon />
        <span>Chọn đáp án phù hợp cho từng mục bên dưới để hoàn thiện yêu cầu.</span>
      </div>
      <div className="matrix-dropdown-heading">
        <strong>Mệnh đề / Nội dung</strong>
        <strong>Đáp án của bạn</strong>
      </div>
      <div className="matrix-dropdown-list">
        {matrixRows.map((row, rowIndex) => {
          const selected = selectedRows[row.id] || '';
          const correctValue = correctRows[row.id] || row.correct || row.correct_value || '';
          const isRowCorrect = reviewMode && selected && correctValue && isSameMatrixOption(selected, correctValue);
          const isRowWrong = reviewMode && (!selected || (correctValue && !isSameMatrixOption(selected, correctValue)));
          return (
            <div
              className={`matrix-dropdown-row ${selected ? 'answered' : ''} ${isRowCorrect ? 'row-review-correct' : ''} ${isRowWrong ? 'row-review-wrong' : ''}`}
              key={row.id}
            >
              <div className="matrix-statement">
                <span>{String(rowIndex + 1).padStart(2, '0')}</span>
                <p>{row.statement}</p>
              </div>
              <label className={`matrix-select-field ${isRowCorrect ? 'dropdown-review-correct' : ''} ${isRowWrong ? 'dropdown-review-wrong' : ''}`}>
                <span className="matrix-select-label">Đáp án</span>
                <select
                  aria-label={`Đáp án cho nhận định ${rowIndex + 1}`}
                  disabled={disabled}
                  value={selected}
                  onChange={(event) => onAnswerChange({
                    rows: { ...selectedRows, [row.id]: event.target.value },
                  })}
                >
                  <option value="">Chọn đáp án</option>
                  {(row.options || []).map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {reviewMode && correctValue ? (
                  <small className="dropdown-correct-answer">
                    {isRowCorrect ? '✓ Đúng' : `Đáp án đúng: ${correctValue}`}
                  </small>
                ) : null}
              </label>
            </div>
          );
        })}
      </div>
      <div className={`matrix-completion ${answeredRows === matrixRows.length ? 'complete' : ''}`}>
        <CheckCircleIcon style={{ width: 17, height: 17, minWidth: 17, flexShrink: 0 }} />
        <span>{answeredRows === matrixRows.length ? 'Đã trả lời đầy đủ' : 'Tiến độ trả lời'} <strong>{answeredRows}/{matrixRows.length}</strong> ý</span>
      </div>
    </div>
  );
};

const QuestionRenderer = ({ question, answer, onAnswerChange, disabled = false, result = null }) => {
  if (!question) return null;

  const update = (next) => onAnswerChange(next);
  const reviewMode = Boolean(result);
  const effectiveType = getEffectiveInteractionType(question);
  const answerModelCorrectIds = question.answer_model?.correct_choice_ids || [];
  const resultCorrectIds = result?.correct_answer?.correct_choice_ids || [];
  const correctChoiceIds = answerModelCorrectIds.length ? answerModelCorrectIds : resultCorrectIds;
  const correctChoiceSet = new Set(correctChoiceIds);

  if (effectiveType === 'single_choice') {
    const validChoiceIds = new Set((question.answer_model.choices || []).map((c) => c.id));
    const selected = (answer.selected_choice_ids || []).find((id) => validChoiceIds.has(id)) || '';
    return (
      <div className="choice-list">
        {question.answer_model.choices.map((choice, index) => {
          const isSelected = selected === choice.id;
          const isCorrect = correctChoiceSet.has(choice.id);
          const reviewClass = reviewMode ? (isCorrect ? 'correct-answer' : isSelected ? 'wrong-answer' : '') : '';
          const letter = String.fromCharCode(65 + index);

          return (
            <label className={`choice-row ${reviewClass}`} key={choice.id}>
              <input
                type="radio"
                name={question.id}
                checked={isSelected}
                disabled={disabled}
                onChange={() => update({ selected_choice_ids: [choice.id] })}
              />
              <span
                className="choice-letter-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  borderRadius: '6px',
                  background: reviewClass === 'correct-answer' ? '#10b981' : reviewClass === 'wrong-answer' ? '#ef4444' : isSelected ? '#2563eb' : '#f1f5f9',
                  color: reviewClass || isSelected ? '#ffffff' : '#475569',
                  fontWeight: 700,
                  fontSize: '13px',
                  flexShrink: 0,
                  marginTop: '1px',
                  marginRight: '2px',
                }}
              >
                {letter}
              </span>
              <span>{choice.text}</span>
              {reviewMode && isCorrect && <strong className="choice-review-label">Đáp án đúng</strong>}
              {reviewMode && isSelected && !isCorrect && <strong className="choice-review-label wrong">Bạn đã chọn</strong>}
            </label>
          );
        })}
      </div>
    );
  }

  if (effectiveType === 'multi_select') {
    const validChoiceIds = new Set((question.answer_model.choices || []).map((c) => c.id));
    const selected = (answer.selected_choice_ids || []).filter((id) => validChoiceIds.has(id));
    const required = getChoiceRequiredCount(question) || question.required_count || question.answer_model.required_count;
    const isMaxed = required && selected.length >= required;
    return (
      <div className="choice-list">
        {required && (
          <div
            className={`choice-required-note ${selected.length === required ? 'complete' : ''}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              fontSize: '13px',
              fontWeight: 700,
              padding: '6px 12px',
              borderRadius: '8px',
              marginBottom: '14px',
              background: selected.length === required ? '#ecfdf5' : '#eef6ff',
              border: `1px solid ${selected.length === required ? '#a7f3d0' : '#bfdbfe'}`,
              color: selected.length === required ? '#047857' : '#1e3a8a',
              width: 'fit-content',
            }}
          >
            <CheckCircleIcon
              style={{
                width: '18px',
                height: '18px',
                minWidth: '18px',
                minHeight: '18px',
                maxWidth: '18px',
                maxHeight: '18px',
                flexShrink: 0,
                color: selected.length === required ? '#10b981' : '#2563eb',
              }}
            />
            <span>Chọn đủ <strong>{required}</strong> đáp án · Đã chọn <strong>{Math.min(selected.length, required)}/{required}</strong></span>
          </div>
        )}
        {question.answer_model.choices.map((choice, index) => {
          const checked = selected.includes(choice.id);
          const isCorrect = correctChoiceSet.has(choice.id);
          const reviewClass = reviewMode ? (isCorrect ? 'correct-answer' : checked ? 'wrong-answer' : '') : '';
          const letter = String.fromCharCode(65 + index);
          return (
            <label className={`choice-row ${reviewClass}`} key={choice.id}>
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => {
                  const next = checked
                    ? selected.filter((id) => id !== choice.id)
                    : isMaxed
                      ? selected
                      : [...selected, choice.id];
                  update({ selected_choice_ids: next });
                }}
              />
              <span
                className="choice-letter-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  borderRadius: '6px',
                  background: reviewClass === 'correct-answer' ? '#10b981' : reviewClass === 'wrong-answer' ? '#ef4444' : checked ? '#2563eb' : '#f1f5f9',
                  color: reviewClass || checked ? '#ffffff' : '#475569',
                  fontWeight: 700,
                  fontSize: '13px',
                  flexShrink: 0,
                  marginTop: '1px',
                  marginRight: '2px',
                }}
              >
                {letter}
              </span>
              <span>{choice.text}</span>
              {reviewMode && isCorrect && <strong className="choice-review-label">Đáp án đúng</strong>}
              {reviewMode && checked && !isCorrect && <strong className="choice-review-label wrong">Bạn đã chọn</strong>}
            </label>
          );
        })}
      </div>
    );
  }

  if (effectiveType === 'choice_matrix') {
    const Renderer = shouldUseMatrixDropdown(question) ? MatrixDropdownRenderer : BinaryMatrixReviewRenderer;
    return <Renderer question={question} answer={answer} onAnswerChange={update} disabled={disabled} result={result} />;
  }

  if (effectiveType === 'dropdown_matrix') {
    const rows = answer.rows || {};
    const correctRows = result?.correct_answer?.rows || Object.fromEntries(
      (question.answer_model.rows || []).map((row) => [row.id, row.correct || {}])
    );
    return (
      <div className="dropdown-matrix">
        {question.answer_model.rows.map((row) => (
          <div className="dropdown-matrix-row" key={row.id}>
            <div className="dropdown-statement">{row.statement}</div>
            <div className="dropdown-fields">
              {row.fields.map((field) => {
                const selectedValue = rows[row.id]?.[field.label] || rows[row.id]?.[field.id] || '';
                const correctValue = correctRows[row.id]?.[field.label] || correctRows[row.id]?.[field.id] || '';
                const isWrong = reviewMode && Boolean(correctValue) && String(selectedValue).trim() !== String(correctValue).trim();
                const isCorrect = reviewMode && Boolean(selectedValue) && Boolean(correctValue) && String(selectedValue).trim() === String(correctValue).trim();
                return (
                <label className={`${isWrong ? 'dropdown-review-wrong' : ''} ${isCorrect ? 'dropdown-review-correct' : ''}`} key={field.id || field.label}>
                  <span>{field.label}</span>
                  <select
                    disabled={disabled}
                    value={selectedValue}
                    onChange={(event) => update({
                      rows: {
                        ...rows,
                        [row.id]: {
                          ...(rows[row.id] || {}),
                          [field.label]: event.target.value,
                        },
                      },
                    })}
                  >
                    <option value="">Chọn đáp án</option>
                    {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                  {reviewMode && correctValue ? (
                    <small className="dropdown-correct-answer">
                      {isCorrect ? '✓ Đúng' : `Đáp án đúng: ${correctValue}`}
                    </small>
                  ) : null}
                </label>
              );})}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (effectiveType === 'drag_drop_classification') {
    return shouldUseClassificationDrop(question)
      ? <ClassificationDropRenderer question={question} answer={answer} onAnswerChange={update} disabled={disabled} result={result} />
      : <MatchingRenderer question={question} answer={answer} onAnswerChange={update} disabled={disabled} result={result} />;
  }

  if (effectiveType === 'sequence') {
    return <SequenceRenderer question={question} answer={answer} onAnswerChange={update} disabled={disabled} result={result} />;
  }

  return <p>Chưa hỗ trợ dạng câu hỏi này.</p>;
};

const QuestionCard = ({ question, answer, onAnswerChange, result, disabled }) => {
  const contextText = question.context_text || question.prompt?.context || '';
  let showContext = Boolean(contextText);
  let promptText = question.question_text || '';

  // Chống lặp: nếu câu hỏi chính trùng với nhận định trong hàng (dropdown_matrix / choice_matrix)
  const firstRowStatement = question.answer_model?.rows?.[0]?.statement;
  if (
    firstRowStatement &&
    promptText &&
    (firstRowStatement.trim() === promptText.trim() ||
      promptText.trim().includes(firstRowStatement.trim()))
  ) {
    if (contextText) {
      promptText = contextText;
      showContext = false;
    }
  } else {
    const inlinePrompt = shouldInlinePromptContinuation(contextText, promptText);
    if (inlinePrompt) {
      promptText = `${contextText} ${promptText}`;
      showContext = false;
    }
  }

  const questionRenderKey = `${question.id || question.question_index || 'question'}-${question.question_index || ''}`;
  return (
    <div className={`question-surface ${question.interaction_type === 'drag_drop_classification' ? 'matching-question' : ''}`} key={questionRenderKey}>
      {showContext ? (
        <div className="question-context">{renderPromptText(contextText)}</div>
      ) : null}
      <h2 className="question-text">{renderPromptText(promptText)}</h2>
      {question.images && question.images.length > 0 && (
        <div className="question-images" key={`images-${questionRenderKey}`}>
          {question.images.map((image) => (
            <img src={resolveQuizImageUrl(image.url)} alt={`Câu ${question.question_index}`} key={`${questionRenderKey}-${image.id || image.url}`} />
          ))}
        </div>
      )}
      <QuestionRenderer
        key={`renderer-${questionRenderKey}`}
        question={question}
        answer={answer}
        onAnswerChange={onAnswerChange}
        disabled={disabled}
        result={result}
      />
      {result && (
        <MistakeCorrectionPanel
          question={question}
          answer={answer}
          result={result}
        />
      )}
    </div>
  );
};

const QuizApp = () => {
  const { currentUser, logout, updateCurrentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const assignmentId = searchParams.get('assignment');
  const requestedDatasetId = searchParams.get('dataset_id') || searchParams.get('datasetId');
  const requestedTopic = searchParams.get('topic');
  const requestedMode = searchParams.get('mode');
  const requestedReplaceActive = searchParams.get('replace') === '1';
  const requestedSessionId = searchParams.get('session_id');
  const requestedAttemptId = searchParams.get('attempt_id');
  const requestedResume = searchParams.get('resume') === '1';
  const requestedRandomParam = searchParams.get('random');
  const [datasets, setDatasets] = useState([]);
  const [datasetId, setDatasetId] = useState(requestedDatasetId || '');
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState('');
  const [mode, setMode] = useState(requestedMode === 'exam' || requestedAttemptId ? 'exam' : (requestedMode === 'smart-review' ? 'smart-review' : 'study'));
  const [isRandomStudy, setIsRandomStudy] = useState(() => requestedRandomParam === '1');
  const [rawStudyQuestions, setRawStudyQuestions] = useState([]);
  const [pastExamAttempts, setPastExamAttempts] = useState([]);
  const isRandomStudyRef = useRef(isRandomStudy);
  const studyCompletedRef = useRef(false);
  useEffect(() => {
    isRandomStudyRef.current = isRandomStudy;
  }, [isRandomStudy]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState({});
  const [marked, setMarked] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState(DEFAULT_DURATION_SECONDS);
  const [examResult, setExamResult] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [, setSaveState] = useState('');
  const [hintVisible, setHintVisible] = useState(false);
  const [activityStarted, setActivityStarted] = useState(Boolean(requestedDatasetId || assignmentId || requestedAttemptId));
  const [pendingResumePrompt, setPendingResumePrompt] = useState(null);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [submittingExam, setSubmittingExam] = useState(false);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [examExitIntent, setExamExitIntent] = useState(null);
  const [savingManual, setSavingManual] = useState(false);
  const [manualSaveNotice, setManualSaveNotice] = useState('');
  const [showQuestionReview, setShowQuestionReview] = useState(false);
  const [resultFilter, setResultFilter] = useState('all');
  const [smartReviewStart, setSmartReviewStart] = useState(null);

  // Quota calculation (Số lượt thi thử còn lại)
  const remainingQuota = useMemo(() => getExamQuotaStatus(currentUser), [currentUser]);

  const isExamDisabled = Boolean(remainingQuota?.isEmpty);
  const isPracticeMode = mode === 'study' || mode === 'smart-review';

  const effectiveUserId = useMemo(() => {
    if (currentUser?.id) return String(currentUser.id);
    try {
      const stored = JSON.parse(localStorage.getItem('user') || 'null');
      if (stored?.id) return String(stored.id);
    } catch {}
    return null;
  }, [currentUser?.id]);

  const getStudyStorageKey = useCallback((dId, top) => {
    if (!effectiveUserId) return null;
    return `sao_viet_study_v5_${effectiveUserId}_${dId || 'default'}_${top || 'all'}`;
  }, [effectiveUserId]);

  const getLegacyStudyStorageKey = useCallback((dId, top) => {
    if (!effectiveUserId) return null;
    return `sao_viet_study_v5_${effectiveUserId}_${dId || 'default'}_${top || 'all'}`;
  }, [effectiveUserId]);

  const clearStudyStorageForDataset = useCallback((targetDatasetId) => {
    try {
      const k1 = getStudyStorageKey(targetDatasetId, topic);
      const k2 = getStudyStorageKey(targetDatasetId, 'all');
      const k3 = getLegacyStudyStorageKey(targetDatasetId, topic);
      if (k1) localStorage.removeItem(k1);
      if (k2) localStorage.removeItem(k2);
      if (k3) localStorage.removeItem(k3);
      if (effectiveUserId && targetDatasetId) {
        const prefix = `sao_viet_study_v5_${effectiveUserId}_`;
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith(prefix) && k.includes(targetDatasetId)) {
            localStorage.removeItem(k);
          }
        }
      }
    } catch (e) {}
  }, [effectiveUserId, getLegacyStudyStorageKey, getStudyStorageKey, topic]);

  const formatSavedDate = (isoStr) => {
    if (!isoStr) return 'Gần đây';
    try {
      const d = new Date(isoStr);
      const time = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
      const date = d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return `${time} ngày ${date}`;
    } catch {
      return 'Gần đây';
    }
  };

  const getCleanOfficialHint = (q) => {
    if (!q) return '';
    // Priority: dedicated thinking hint (non-spoiler) first, fallback to pedagogical feedback
    const raw = q.hint || q.prompt?.hint || q.feedback || q.prompt?.feedback || '';
    if (!raw || typeof raw !== 'string') return '';
    return cleanExplanationText(raw.trim(), q);
  };

  const currentQuestion = questions[currentIndex];
  const currentQuestionId = currentQuestion?.id || '';
  const selectedDataset = datasets.find((dataset) => dataset.id === datasetId);
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] || emptyAnswerFor(currentQuestion) : {};
  const currentResult = currentQuestion ? results[currentQuestion.id] : null;
  const currentReviewResult = currentResult
    || (currentQuestion && examResult?.correct?.find((row) => row.question_id === currentQuestion.id))
    || (currentQuestion && examResult?.incorrect?.find((row) => row.question_id === currentQuestion.id))
    || (currentQuestion && examResult?.details?.find((row) => row.question_id === currentQuestion.id))
    || null;
  const currentHint = getCleanOfficialHint(currentQuestion);
  const isQuestionDone = (question) => {
    if (!question) return false;
    if (isPracticeMode && results[question.id]) return true;
    return hasAnswer(question, answers[question.id]);
  };
  const answeredCount = questions.reduce((count, question) => count + (isQuestionDone(question) ? 1 : 0), 0);
  const progressPercent = questions.length ? Math.round((answeredCount * 100) / questions.length) : 0;
  const isActiveExam = mode === 'exam' && Boolean(sessionId) && !examResult;

  useEffect(() => {
    if (!requestedDatasetId && !assignmentId && !requestedMode && !requestedAttemptId) {
      navigate('/', { replace: true });
    }
  }, [requestedDatasetId, assignmentId, requestedMode, requestedAttemptId, navigate]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (requestedMode === 'study' || requestedMode === 'smart-review') {
        setMode(requestedMode);
        setSessionId(null);
        if (requestedDatasetId) {
          setDatasetId(requestedDatasetId);
          setActivityStarted(true);
        } else {
          setActivityStarted(false);
        }
      } else if (requestedMode === 'exam' || requestedAttemptId) {
        setMode('exam');
        if (requestedDatasetId || requestedAttemptId) {
          if (requestedDatasetId) setDatasetId(requestedDatasetId);
          setActivityStarted(true);
        } else {
          setActivityStarted(false);
        }
      } else {
        if (saved.marked) setMarked(saved.marked);
        if (saved.mode) setMode(saved.mode);
        if (saved.datasetId) setDatasetId(saved.datasetId);
        if (saved.topic) setTopic(saved.topic);
        if (saved.remainingSeconds) setRemainingSeconds(saved.remainingSeconds);
        if (saved.sessionId) setSessionId(saved.sessionId);
        if (saved.activityStarted || saved.sessionId) setActivityStarted(true);
      }
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [requestedDatasetId, requestedMode, requestedAttemptId]);

  useEffect(() => {
    if (mode === 'exam') {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [mode]);

  const applySession = useCallback((session) => {
    setSessionId(session.id);
    setDatasetId(session.dataset_id);
    setMode('exam');
    setActivityStarted(true);

    const rawExamList = Array.isArray(session.questions) ? session.questions : [];
    const examOrderKey = `${EXAM_ORDER_STORAGE_PREFIX}_${session.id}`;
    let savedExamMeta = null;
    try {
      const rawMeta = localStorage.getItem(examOrderKey);
      if (rawMeta) {
        savedExamMeta = JSON.parse(rawMeta);
      }
    } catch (e) {
      savedExamMeta = null;
    }

    let examQuestions = [];
    if (savedExamMeta && Array.isArray(savedExamMeta.questionOrder) && savedExamMeta.questionOrder.length > 0) {
      examQuestions = randomizeQuestionList(rawExamList, true, savedExamMeta);
    } else {
      const isAlreadyShuffledByBackend = rawExamList.some(
        (q, idx) => idx > 0 && (Number(q.question_index) || 0) < (Number(rawExamList[idx - 1].question_index) || 0)
      );
      if (isAlreadyShuffledByBackend) {
        examQuestions = rawExamList.map((q) => randomizeQuestion(q, true));
      } else {
        examQuestions = randomizeQuestionList(rawExamList, true, null);
      }
      try {
        localStorage.setItem(examOrderKey, JSON.stringify(extractQuestionOrderMeta(examQuestions)));
      } catch (e) {}
    }

    setQuestions(examQuestions);
    setAnswers(session.answers || {});
    setMarked(Object.fromEntries((session.marked_question_ids || []).map((id) => [id, true])));
    setCurrentIndex(Math.min(session.current_index || 0, Math.max(0, examQuestions.length - 1)));
    setRemainingSeconds(session.remaining_seconds);
    setExamResult(null);
  }, []);

  const applyAttemptReview = useCallback(async (attemptData, fallbackDatasetId = '') => {
    if (!attemptData) return;
    const targetDsId = attemptData.dataset_id || fallbackDatasetId;
    setSessionId(null);
    if (targetDsId) {
      setDatasetId(targetDsId);
    }
    setMode('exam');
    setActivityStarted(true);

    let rawList = Array.isArray(attemptData.questions) ? attemptData.questions : [];
    if (!rawList.length && targetDsId) {
      try {
        rawList = await getQuizQuestions({ datasetId: targetDsId });
      } catch (e) {
        rawList = [];
      }
    }

    let reviewQuestions = rawList.map((q) => normalizeQuestion(q));
    if (attemptData.session_id) {
      try {
        const rawMeta = localStorage.getItem(`${EXAM_ORDER_STORAGE_PREFIX}_${attemptData.session_id}`);
        if (rawMeta) {
          const savedMeta = JSON.parse(rawMeta);
          if (savedMeta && Array.isArray(savedMeta.questionOrder) && savedMeta.questionOrder.length > 0) {
            reviewQuestions = randomizeQuestionList(rawList, true, savedMeta);
          }
        }
      } catch (e) {}
    }

    const nextResults = {};
    (attemptData.details || []).forEach((row) => {
      nextResults[row.question_id] = row;
    });
    (attemptData.correct || []).forEach((row) => {
      nextResults[row.question_id] = row;
    });
    (attemptData.incorrect || []).forEach((row) => {
      nextResults[row.question_id] = row;
    });

    setQuestions(reviewQuestions);
    setAnswers(attemptData.answers || {});
    setResults(nextResults);
    setMarked(Object.fromEntries((attemptData.marked_question_ids || []).map((id) => [id, true])));
    setCurrentIndex(0);
    setExamResult(attemptData);
    setShowQuestionReview(false);
    setResultFilter('all');
  }, []);

  useEffect(() => {
    const loadBase = async () => {
      try {
        setLoading(true);
        const [datasetRows, freshUser, attemptHistoryRows] = await Promise.all([
          getQuizDatasets(),
          getCurrentUser().catch(() => null),
          getAttempts(50, true).catch(() => []),
        ]);
        if (freshUser && updateCurrentUser) {
          updateCurrentUser(freshUser);
        }
        setDatasets(datasetRows);
        setPastExamAttempts(Array.isArray(attemptHistoryRows) ? attemptHistoryRows.filter((r) => r.mode !== 'study') : []);
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        const defaultDataset = requestedDatasetId
          || (saved.datasetId && datasetRows.some((row) => row.id === saved.datasetId) ? saved.datasetId : datasetRows[0]?.id || '');
        setDatasetId(defaultDataset);
        if (requestedAttemptId) {
          try {
            const attemptData = await getAttempt(requestedAttemptId);
            const dsId = attemptData?.dataset_id || defaultDataset;
            if (dsId) {
              const topicRows = await getQuizTopics(dsId).catch(() => []);
              setTopics(topicRows);
            }
            await applyAttemptReview(attemptData, defaultDataset);
            return;
          } catch (attErr) {
            const msg = attErr.response?.data?.detail || attErr.message || 'Không tải được lịch sử bài thi.';
            setError(msg);
          }
        }
        if (defaultDataset) {
          const topicRows = await getQuizTopics(defaultDataset);
          setTopics(topicRows);
          if (assignmentId) {
            const session = await startExamSession({ dataset_id: defaultDataset, assignment_id: assignmentId });
            applySession(session);
          } else if (requestedMode === 'exam' && requestedDatasetId) {
            try {
              if (requestedSessionId) {
                try {
                  const existingSession = await getExamSession(requestedSessionId);
                  if (existingSession && existingSession.status === 'in_progress') {
                    applySession(existingSession);
                    return;
                  }
                } catch (resumeErr) {
                  console.warn('Could not resume specific session, falling back to start/active:', resumeErr);
                }
              }
              const targetDs = datasetRows.find((d) => d.id === requestedDatasetId) || datasetRows[0];
              const newSession = await startExamSession({
                dataset_id: targetDs.id,
                duration_minutes: DEFAULT_DURATION_SECONDS / 60,
                question_count: Math.min(50, targetDs.total_questions || 50),
                pass_percent: 70,
                shuffle_questions: true,
                replace_active: requestedReplaceActive,
              });
              applySession(newSession);
            } catch (startErr) {
              const msg = startErr.response?.data?.detail || startErr.message || 'Không thể bắt đầu bài thi thử.';
              setError(msg);
              alert(msg);
              setMode('study');
              setActivityStarted(false);
              setSearchParams({ dataset_id: requestedDatasetId, mode: 'study' }, { replace: true });
            }
          } else if (requestedMode === 'smart-review' || ((requestedMode === 'study' || requestedMode === 'practice') && requestedDatasetId)) {
            setMode(requestedMode === 'smart-review' ? 'smart-review' : 'study');
            setSessionId(null);
            setActivityStarted(true);
          } else if (saved.sessionId && saved.mode === 'exam') {
            localStorage.removeItem(STORAGE_KEY);
            setSessionId(null);
            setMode('study');
            setActivityStarted(false);
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Không tải được dữ liệu quiz.');
      } finally {
        setLoading(false);
      }
    };
    loadBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyAttemptReview, applySession, assignmentId]);

  const loadQuestions = useCallback(async () => {
    if ((!datasetId && mode !== 'smart-review') || assignmentId || sessionId || !activityStarted || !isPracticeMode) return;
    try {
      setLoading(true);
      setExamResult(null);
      studyCompletedRef.current = false;
      const rows = mode === 'smart-review'
        ? await getReviewQuestions({ datasetId: requestedDatasetId || undefined, topic: requestedTopic || topic || undefined, state: 'weak', limit: 20 })
        : await getQuizQuestions({ datasetId, topic });
      setError('');
      setRawStudyQuestions(rows);

      // Fresh sequential questions from API (sorted strictly 1..50 by question_index, 100% original order)
      const sequentialQuestions = randomizeQuestionList(rows, false);
      const validQuestionIds = new Set(sequentialQuestions.map((q) => q.id || q.question_id));

      // 1. Smart review mode
      if (mode === 'smart-review') {
        const shouldShuffleSmart = requestedRandomParam === '0'
          ? false
          : (requestedRandomParam === '1' ? true : isRandomStudyRef.current);
        setIsRandomStudy(shouldShuffleSmart);
        setSmartReviewStart(await getLearningSummary().catch(() => null));
        setQuestions(randomizeQuestionList(rows, shouldShuffleSmart, null));
        setAnswers({});
        setResults({});
        setMarked({});
        setCurrentIndex(0);
        setPendingResumePrompt(null);
        return;
      }

      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (
            k &&
            (k.startsWith('sao_viet_study_v3_') ||
              k.startsWith('sao_viet_study_v4_') ||
              k.startsWith('sao_viet_study_v5_guest_') ||
              k === 'sao_viet_quiz_state_v3')
          ) {
            localStorage.removeItem(k);
          }
        }
      } catch (e) {}

      const v4Key = getStudyStorageKey(datasetId, topic);
      let saved = null;
      if (v4Key && effectiveUserId) {
        try {
          const parsed = JSON.parse(localStorage.getItem(v4Key) || 'null');
          if (parsed && typeof parsed === 'object' && (!parsed.ownerUserId || String(parsed.ownerUserId) === String(effectiveUserId))) {
            saved = parsed;
          }
        } catch (e) {
          saved = null;
        }
      }

      if (!saved && effectiveUserId) {
        const fallbackAllKey = getStudyStorageKey(datasetId, 'all');
        if (fallbackAllKey && fallbackAllKey !== v4Key) {
          try {
            const raw = localStorage.getItem(fallbackAllKey);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && typeof parsed === 'object' && (!parsed.ownerUserId || String(parsed.ownerUserId) === String(effectiveUserId))) {
                saved = parsed;
              }
            }
          } catch {}
        }
      }

      // If v5 exists, re-filter against fresh API questions, normalize savedAt, and re-persist clean payload
      if (saved && typeof saved === 'object' && v4Key && effectiveUserId) {
        const freshQuestionMap = new Map(sequentialQuestions.map((q) => [q.id || q.question_id, q]));
        const filteredAnswers = {};
        const filteredResults = {};
        const filteredMarked = {};

        Object.entries(saved.answers || {}).forEach(([qid, ans]) => {
          if (validQuestionIds.has(qid)) filteredAnswers[qid] = ans;
        });
        Object.entries(saved.results || {}).forEach(([qid, res]) => {
          if (validQuestionIds.has(qid) && res) {
            const freshQ = freshQuestionMap.get(qid);
            const freshFeedback = freshQ?.feedback || freshQ?.prompt?.feedback || res.feedback || '';
            const freshHint = freshQ?.hint || freshQ?.prompt?.hint || res.hint || '';
            filteredResults[qid] = {
              ...res,
              feedback: freshFeedback,
              hint: freshHint,
            };
          }
        });
        if (Array.isArray(saved.marked)) {
          saved.marked.forEach((qid) => {
            if (validQuestionIds.has(qid)) filteredMarked[qid] = true;
          });
        } else if (saved.marked && typeof saved.marked === 'object') {
          Object.entries(saved.marked).forEach(([qid, mrk]) => {
            if (validQuestionIds.has(qid) && mrk) filteredMarked[qid] = true;
          });
        }

        const isStaleCompletedSession = Boolean(
          saved.completed === true ||
          (
            sequentialQuestions.length > 0 &&
            Object.keys(filteredAnswers).length >= sequentialQuestions.length &&
            Object.keys(filteredResults).length >= sequentialQuestions.length
          )
        );

        if (isStaleCompletedSession) {
          clearStudyStorageForDataset(datasetId);
          saved = null;
        } else {
          const savedIsRandom = typeof saved.isRandom === 'boolean' ? saved.isRandom : false;
          const savedQuestionOrder = savedIsRandom && Array.isArray(saved.questionOrder)
            ? saved.questionOrder.filter((qid) => validQuestionIds.has(qid))
            : [];
          const savedChoiceOrders = savedIsRandom && saved.choiceOrders && typeof saved.choiceOrders === 'object'
            ? saved.choiceOrders
            : {};

          const firstQId = savedQuestionOrder[0] || sequentialQuestions[0]?.id || sequentialQuestions[0]?.question_id || '';
          let validCurQId = saved.currentQuestionId;
          if (!validCurQId || !validQuestionIds.has(validCurQId)) {
            validCurQId = firstQId;
          }

          let normalizedSavedAt = new Date().toISOString();
          if (saved.savedAt) {
            try {
              const parsed = new Date(saved.savedAt);
              if (!isNaN(parsed.getTime())) {
                normalizedSavedAt = parsed.toISOString();
              }
            } catch (e) {}
          }

          const cleanedV4Payload = {
            ownerUserId: String(effectiveUserId),
            answers: filteredAnswers,
            results: filteredResults,
            marked: filteredMarked,
            currentQuestionId: validCurQId,
            isRandom: savedIsRandom,
            questionOrder: savedQuestionOrder,
            choiceOrders: savedChoiceOrders,
            savedAt: normalizedSavedAt,
            datasetId,
            topic,
            dataRevision: 'v5',
          };

          try {
            localStorage.setItem(v4Key, JSON.stringify(cleanedV4Payload));
            saved = cleanedV4Payload;
          } catch (writeErr) {
            console.warn('Could not re-persist cleaned v5 payload:', writeErr);
            saved = cleanedV4Payload;
          }
        }
      }

      // 3. Purge obsolete shuffle caches
      try {
        localStorage.removeItem('sv_exam_shuffled_questions_v2');
        sessionStorage.removeItem('sv_exam_shuffled_questions_v2');
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith('sv_exam_shuffled_') || k.startsWith('EXAM_SHUFFLE_CACHE')) {
            localStorage.removeItem(k);
          }
        });
        Object.keys(sessionStorage).forEach((k) => {
          if (k.startsWith('sv_exam_shuffled_') || k.startsWith('EXAM_SHUFFLE_CACHE')) {
            sessionStorage.removeItem(k);
          }
        });
      } catch (cleanErr) {
        // ignore
      }

      // 4. Determine resumable progress & build ordered question list
      const firstSeqQId = sequentialQuestions[0]?.id || sequentialQuestions[0]?.question_id || '';
      const firstSavedOrderQId = Array.isArray(saved?.questionOrder) && saved.questionOrder.length > 0
        ? saved.questionOrder[0]
        : firstSeqQId;
      const hasMovedQuestion = Boolean(
        saved?.currentQuestionId
        && validQuestionIds.has(saved.currentQuestionId)
        && saved.currentQuestionId !== firstSavedOrderQId
      );
      const hasResumableProgress = Boolean(
        saved && (
          Object.keys(saved.answers || {}).length > 0 ||
          Object.keys(saved.results || {}).length > 0 ||
          Object.keys(saved.marked || {}).length > 0 ||
          hasMovedQuestion
        )
      );

      if (hasResumableProgress) {
        const savedIsRandom = Boolean(saved.isRandom);
        const resumedQuestions = savedIsRandom
          ? randomizeQuestionList(rows, true, {
              questionOrder: saved.questionOrder,
              choiceOrders: saved.choiceOrders,
            })
          : sequentialQuestions;

        setQuestions(resumedQuestions);

        if (requestedResume) {
          setIsRandomStudy(savedIsRandom);
          setAnswers(saved.answers || {});
          setResults(saved.results || {});
          setMarked(saved.marked || {});
          let target = 0;
          if (saved.currentQuestionId) {
            const foundIdx = resumedQuestions.findIndex((q) => (q.id || q.question_id) === saved.currentQuestionId);
            if (foundIdx >= 0) target = foundIdx;
          } else if (typeof saved.currentIndex === 'number') {
            target = Math.min(Math.max(0, saved.currentIndex), Math.max(0, resumedQuestions.length - 1));
          }
          setCurrentIndex(target);
          setActivityStarted(true);
          const restoredCount = new Set([
            ...Object.keys(saved.answers || {}),
            ...Object.keys(saved.results || {}),
          ]).size;
          setPendingResumePrompt(null);
          setSaveState(`✓ Đã khôi phục ${restoredCount} câu (${savedIsRandom ? 'Trộn ngẫu nhiên' : 'Theo thứ tự đề'} · Câu ${target + 1})`);
        } else {
          const nextRandomChoice = requestedRandomParam === '0'
            ? false
            : (requestedRandomParam === '1' ? true : savedIsRandom);
          setIsRandomStudy(nextRandomChoice);
          setPendingResumePrompt(saved);
        }
      } else {
        const shouldShuffleNow = requestedRandomParam === '1';
        setIsRandomStudy(shouldShuffleNow);
        try {
          if (v4Key) localStorage.removeItem(v4Key);
        } catch (e) {}
        const orderedQuestions = shouldShuffleNow
          ? randomizeQuestionList(rows, true, null)
          : sequentialQuestions;
        setQuestions(orderedQuestions);
        setCurrentIndex(0);
        setAnswers({});
        setResults({});
        setMarked({});
        setSaveState(
          shouldShuffleNow
            ? 'Chế độ: Trộn ngẫu nhiên câu hỏi & đáp án'
            : 'Chế độ: Theo thứ tự bộ đề'
        );
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Không tải được câu hỏi.');
    } finally {
      setLoading(false);
    }
  }, [activityStarted, assignmentId, clearStudyStorageForDataset, datasetId, effectiveUserId, getLegacyStudyStorageKey, getStudyStorageKey, isPracticeMode, mode, requestedRandomParam, requestedResume, requestedTopic, sessionId, topic]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    if (!datasetId || assignmentId || sessionId) return undefined;
    let active = true;
    getQuizTopics(datasetId)
      .then((rows) => {
        if (active) {
          setTopics(rows);
          setTopic(rows.some((row) => row.slug === requestedTopic) ? requestedTopic : '');
        }
      })
      .catch(() => active && setTopics([]));
    return () => { active = false; };
  }, [assignmentId, datasetId, requestedTopic, sessionId]);

  useEffect(() => {
    if (mode !== 'exam' || examResult) return undefined;
    const timer = window.setInterval(() => {
      setRemainingSeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [mode, examResult]);

  useEffect(() => {
    if (mode === 'exam' && remainingSeconds === 0 && questions.length && !examResult) {
      handleSubmitExam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingSeconds, mode, questions.length, examResult]);

  useEffect(() => {
    if (!isActiveExam) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isActiveExam]);

  useEffect(() => {
    setHintVisible(false);
  }, [currentQuestionId, mode]);

  useEffect(() => {
    if (!currentQuestionId || typeof window === 'undefined' || window.innerWidth > 820) return;
    window.requestAnimationFrame(() => {
      document.querySelector('.quiz-panel')?.scrollIntoView({ block: 'start' });
    });
  }, [currentQuestionId, currentIndex]);

  const setCurrentAnswer = (nextAnswer) => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: nextAnswer }));
    if (isPracticeMode) {
      setResults((prev) => {
        const next = { ...prev };
        delete next[currentQuestion.id];
        return next;
      });
    }
  };

  const handleCheck = async () => {
    if (!currentQuestion) return;
    let payloadAnswer = currentAnswer;
    if (
      currentQuestion.interaction_type === 'sequence' &&
      (!currentAnswer?.order || currentAnswer.order.length === 0)
    ) {
      payloadAnswer = {
        order: currentQuestion.answer_model?.steps || [],
        sequence_touched: true,
      };
      setAnswers((prev) => ({ ...prev, [currentQuestion.id]: payloadAnswer }));
    }
    const result = await gradeQuestion(currentQuestion.id, payloadAnswer);
    setHintVisible(false);
    setResults((prev) => ({ ...prev, [currentQuestion.id]: result }));
  };

  const handleRetryQuestion = () => {
    if (!currentQuestion || !isPracticeMode) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: emptyAnswerFor(currentQuestion) }));
    setResults((prev) => {
      const next = { ...prev };
      delete next[currentQuestion.id];
      return next;
    });
    setHintVisible(false);
    setSaveState('Đã mở lại câu để chọn đáp án');
  };

  const handleNextQuestion = async () => {
    if (!currentQuestion) return;
    if (isPracticeMode && !currentResult) {
      await handleCheck();
      return;
    }
    const nextIndex = Math.min(questions.length - 1, currentIndex + 1);
    setCurrentIndex(nextIndex);
  };

  const getNextButtonLabel = () => {
    if (isPracticeMode && !currentResult) return 'Xem đúng/sai';
    if (currentIndex === questions.length - 1) return 'Đã xong';
    return 'Câu tiếp theo';
  };

  const performSubmitExam = async (afterSubmit) => {
    if (!datasetId || !questions.length || submittingExam) return;
    try {
      setSubmittingExam(true);
      setShowSubmitConfirmModal(false);
      setExamExitIntent(null);
      setLoading(true);
      setError('');
      let result;
      if (mode === 'exam' && sessionId) {
        await saveExamSession(sessionId, {
          answers,
          marked_question_ids: Object.keys(marked).filter((id) => marked[id]),
          current_index: currentIndex,
          remaining_seconds: remainingSeconds,
        });
        result = await submitExamSession(sessionId);
        try {
          sessionStorage.removeItem(`${EXAM_SHUFFLE_CACHE_PREFIX}_${sessionId}`);
          localStorage.removeItem(`${EXAM_SHUFFLE_CACHE_PREFIX}_${sessionId}`);
        } catch (e) {}
      } else {
        studyCompletedRef.current = true;
        clearStudyStorageForDataset(datasetId);
        const answerPayload = questions.map((q) => ({
          question_id: q.id,
          answer: answers[q.id] || emptyAnswerFor(q),
          marked_for_review: Boolean(marked[q.id]),
        }));
        result = await submitExam({
          datasetId,
          answers: answerPayload,
          durationSeconds: DEFAULT_DURATION_SECONDS - remainingSeconds,
          passPercent: 70,
          mode: isPracticeMode ? mode : 'exam',
        });
        clearStudyStorageForDataset(datasetId);
      }
      if (mode === 'smart-review') {
        const afterSummary = await getLearningSummary().catch(() => null);
        const removed = Math.max(0, Number(smartReviewStart?.weak_count || 0) - Number(afterSummary?.weak_count || 0));
        result = {
          ...result,
          smart_review: {
            correct: result.correct_questions || 0,
            progressed: Math.max(0, Number(result.correct_questions || 0) - removed),
            mastered: removed,
            remaining: Number(afterSummary?.weak_count || 0),
          },
        };
      }
      setExamResult(result);
      const nextResults = {};
      (result.details || []).forEach((row) => {
        nextResults[row.question_id] = row;
      });
      (result.correct || []).forEach((row) => {
        nextResults[row.question_id] = row;
      });
      (result.incorrect || []).forEach((row) => {
        nextResults[row.question_id] = row;
      });
      if (!result.correct?.length && !result.details?.length && questions.length) {
        const incorrectIds = new Set((result.incorrect || []).map((r) => r.question_id));
        questions.forEach((q) => {
          if (!incorrectIds.has(q.id) && !nextResults[q.id]) {
            nextResults[q.id] = {
              question_id: q.id,
              is_correct: true,
              feedback: q.feedback || q.hint || 'Bạn đã trả lời chính xác câu hỏi này.',
            };
          }
        });
      }
      setResults(nextResults);
      setShowQuestionReview(false);
      setResultFilter('all');
      setSaveState(isPracticeMode ? 'Đã hoàn thành phiên ôn tập' : 'Đã nộp bài và chấm điểm');

      // Refresh user account quota & attempt history immediately
      try {
        const [freshUser, freshAttempts] = await Promise.all([
          getCurrentUser().catch(() => null),
          getAttempts(50, true).catch(() => null),
        ]);
        if (freshUser && updateCurrentUser) {
          updateCurrentUser(freshUser);
        }
        if (Array.isArray(freshAttempts)) {
          setPastExamAttempts(freshAttempts.filter((r) => r.mode !== 'study'));
        }
      } catch (userErr) {
        console.warn('Could not refresh current user upon submit:', userErr);
      }
      if (typeof afterSubmit === 'function') {
        afterSubmit(result);
      }
    } catch (err) {
      if (isPracticeMode || mode !== 'exam' || !sessionId) {
        studyCompletedRef.current = false;
      }
      console.error('Error in performSubmitExam:', err);
      setError(err.response?.data?.detail || err.message || 'Không nộp được bài thi.');
    } finally {
      setLoading(false);
      setSubmittingExam(false);
    }
  };

  const handleRequestSubmit = ({ force = false } = {}) => {
    if (!datasetId || !questions.length || submittingExam) return;
    if (!force && answeredCount < questions.length) {
      setShowSubmitConfirmModal(true);
    } else {
      performSubmitExam();
    }
  };

  const handleSubmitExam = () => handleRequestSubmit({ force: true });

  const requestExamExit = (intent) => {
    if (isActiveExam) {
      setExamExitIntent(intent);
      return;
    }
    if (typeof intent?.action === 'function') {
      intent.action();
    }
  };

  const handleSaveAndExitExam = async () => {
    const targetAction = examExitIntent?.action;
    if (mode === 'exam' && sessionId) {
      try {
        setLoading(true);
        await saveExamSession(sessionId, {
          answers,
          marked_question_ids: Object.keys(marked).filter((id) => marked[id]),
          current_index: currentIndex,
          remaining_seconds: remainingSeconds,
        });
      } catch (err) {
        console.warn('Could not save exam session on exit:', err);
      } finally {
        setLoading(false);
      }
    } else if (mode === 'study') {
      saveStudyProgress(answers, results, marked, currentIndex);
    }
    setExamExitIntent(null);
    if (typeof targetAction === 'function') {
      targetAction();
    } else {
      navigate('/', { replace: true });
    }
  };

  const confirmExamExit = () => {
    const intent = examExitIntent;
    if (!intent) return;
    performSubmitExam(() => {
      if (typeof intent.action === 'function') {
        intent.action();
      }
    });
  };

  const handleManualSaveExam = async () => {
    if (savingManual) return;
    try {
      setSavingManual(true);
      if (mode === 'exam' && sessionId) {
        await saveExamSession(sessionId, {
          answers,
          marked_question_ids: Object.keys(marked).filter((id) => marked[id]),
          current_index: currentIndex,
          remaining_seconds: remainingSeconds,
        });
      } else if (mode === 'study') {
        saveStudyProgress(answers, results, marked, currentIndex);
      }
      const nowStr = new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
      setManualSaveNotice(`Đã lưu (${nowStr})`);
      setSaveState(`Đã lưu tiến trình lúc ${nowStr}`);
      setTimeout(() => {
        setManualSaveNotice('');
      }, 4000);
    } catch (err) {
      console.warn('Manual save failed:', err);
      setManualSaveNotice('Lưu chưa thành công');
      setTimeout(() => setManualSaveNotice(''), 3000);
    } finally {
      setSavingManual(false);
    }
  };

  useEffect(() => {
    if (!isActiveExam || !sessionId) return undefined;
    const handlePageHide = () => {
      try {
        const payload = JSON.stringify({
          answers,
          marked_question_ids: Object.keys(marked).filter((id) => marked[id]),
          current_index: currentIndex,
          remaining_seconds: remainingSeconds,
        });
        const token = localStorage.getItem('sao_viet_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
        fetch(`/api/v1/training/exam/sessions/${sessionId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: payload,
          keepalive: true,
        });
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [isActiveExam, sessionId, answers, marked, currentIndex, remainingSeconds]);

  useEffect(() => {
    if (!isActiveExam) return undefined;
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setExamExitIntent({
        title: 'Tạm dừng & Quay lại sảnh?',
        description: 'Lưu lại tiến trình hiện tại và quay lại sảnh mà không đóng bài thi.',
        action: () => navigate('/'),
      });
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isActiveExam, navigate]);

  useEffect(() => {
    if (mode !== 'exam' || !sessionId || !questions.length || !activityStarted || examResult) return undefined;
    const timeout = window.setTimeout(async () => {
      try {
        await saveExamSession(sessionId, {
          answers,
          marked_question_ids: Object.keys(marked).filter((id) => marked[id]),
          current_index: currentIndex,
          remaining_seconds: remainingSeconds,
        });
      } catch (e) {}
    }, 2000);
    return () => window.clearTimeout(timeout);
  }, [activityStarted, answers, currentIndex, examResult, marked, mode, questions.length, remainingSeconds, sessionId]);

  const saveStudyProgress = useCallback((ans, res, mrk, idx, overrideQuestions = null, overrideRandom = null) => {
    if (mode !== 'study' || !datasetId || examResult || submittingExam || studyCompletedRef.current) return;
    const activeQuestions = overrideQuestions || questions;
    if (!activeQuestions.length) return;
    const activeRandom = typeof overrideRandom === 'boolean' ? overrideRandom : isRandomStudy;
    const currentIdx = idx !== undefined ? idx : currentIndex;
    const hasAnyProgress = (
      Object.keys(ans || {}).length > 0 ||
      Object.keys(res || {}).length > 0 ||
      Object.keys(mrk || {}).length > 0 ||
      currentIdx > 0
    );
    if (!hasAnyProgress) return;

    const key = getStudyStorageKey(datasetId, topic);
    if (!key || !effectiveUserId) return;
    const doneCount = activeQuestions.reduce((count, q) => {
      return count + (hasAnswer(q, (ans || {})[q.id]) || (res || {})[q.id] ? 1 : 0);
    }, 0);
    if (studyCompletedRef.current && Object.keys(res || {}).length >= activeQuestions.length && doneCount >= activeQuestions.length) {
      return;
    }
    const currentQ = activeQuestions[currentIdx];
    const currentQId = currentQ?.id || currentQ?.question_id || null;
    const orderMeta = activeRandom
      ? extractQuestionOrderMeta(activeQuestions)
      : { questionOrder: [], choiceOrders: {} };

    const payload = {
      ownerUserId: String(effectiveUserId),
      answers: ans || {},
      results: res || {},
      marked: mrk || {},
      currentIndex: currentIdx,
      currentQuestionId: currentQId,
      isRandom: Boolean(activeRandom),
      questionOrder: orderMeta.questionOrder,
      choiceOrders: orderMeta.choiceOrders,
      savedAt: new Date().toISOString(),
      datasetId,
      topic,
      dataRevision: 'v5',
    };
    try {
      localStorage.setItem(key, JSON.stringify(payload));
      setSaveState('✓ Đã lưu');
    } catch (e) {
      console.error('Error saving study session:', e);
    }
  }, [currentIndex, datasetId, effectiveUserId, examResult, getStudyStorageKey, isRandomStudy, mode, questions, submittingExam, topic]);

  useEffect(() => {
    if (mode !== 'study' || !questions.length || !activityStarted || pendingResumePrompt || examResult || submittingExam || studyCompletedRef.current) return undefined;
    const timeout = window.setTimeout(() => {
      saveStudyProgress(answers, results, marked, currentIndex);
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [activityStarted, answers, currentIndex, examResult, marked, mode, pendingResumePrompt, questions.length, results, saveStudyProgress, submittingExam]);

  const handleConfirmResumeStudy = () => {
    if (!pendingResumePrompt) return;
    studyCompletedRef.current = false;
    const savedIsRandom = Boolean(pendingResumePrompt.isRandom);
    setIsRandomStudy(savedIsRandom);
    const baseRows = rawStudyQuestions.length ? rawStudyQuestions : questions;
    const resumedList = savedIsRandom
      ? randomizeQuestionList(baseRows, true, {
          questionOrder: pendingResumePrompt.questionOrder,
          choiceOrders: pendingResumePrompt.choiceOrders,
        })
      : randomizeQuestionList(baseRows, false, null);

    setQuestions(resumedList);
    setAnswers(pendingResumePrompt.answers || {});
    setResults(pendingResumePrompt.results || {});
    setMarked(pendingResumePrompt.marked || {});

    let targetIdx = 0;
    if (pendingResumePrompt.currentQuestionId) {
      const foundIdx = resumedList.findIndex(
        (q) => (q.id || q.question_id) === pendingResumePrompt.currentQuestionId
      );
      if (foundIdx >= 0) targetIdx = foundIdx;
    } else if (typeof pendingResumePrompt.currentIndex === 'number') {
      targetIdx = Math.min(
        Math.max(0, pendingResumePrompt.currentIndex),
        Math.max(0, resumedList.length - 1)
      );
    }

    setCurrentIndex(targetIdx);
    setActivityStarted(true);
    const restoredCount = new Set([
      ...Object.keys(pendingResumePrompt.answers || {}),
      ...Object.keys(pendingResumePrompt.results || {}),
    ]).size;
    setPendingResumePrompt(null);
    setSaveState(`✓ Đã khôi phục ${restoredCount} câu (${savedIsRandom ? 'Trộn ngẫu nhiên' : 'Theo thứ tự đề'} · Câu ${targetIdx + 1})`);
  };

  const handleToggleStudyRandom = (nextRandom) => {
    const normalizedNext = Boolean(nextRandom);
    setIsRandomStudy(normalizedNext);
    const baseRows = rawStudyQuestions.length ? rawStudyQuestions : questions;
    if (!baseRows.length) return;
    const nextQuestions = randomizeQuestionList(baseRows, normalizedNext, null);
    setQuestions(nextQuestions);

    let nextIdx = 0;
    if (currentQuestionId && Object.keys(answers).length > 0) {
      const found = nextQuestions.findIndex((q) => (q.id || q.question_id) === currentQuestionId);
      if (found >= 0) nextIdx = found;
    }
    setCurrentIndex(nextIdx);

    const hasAnyProgress = (
      Object.keys(answers || {}).length > 0 ||
      Object.keys(results || {}).length > 0 ||
      Object.keys(marked || {}).length > 0
    );
    if (hasAnyProgress && mode === 'study' && datasetId && !examResult && !submittingExam && !studyCompletedRef.current) {
      saveStudyProgress(answers, results, marked, nextIdx, nextQuestions, normalizedNext);
    } else if (mode === 'study' && datasetId) {
      clearStudyStorageForDataset(datasetId);
    }

    setSaveState(
      normalizedNext
        ? '✓ Đã bật trộn ngẫu nhiên câu hỏi và đáp án'
        : '✓ Đã chuyển về thứ tự gốc của bộ đề'
    );
  };

  const handleRequestResetAllAnswers = () => {
    if (examResult) {
      executeResetAllAnswers();
    } else {
      setShowResetConfirmModal(true);
    }
  };

  const executeResetAllAnswers = () => {
    studyCompletedRef.current = false;
    clearStudyStorageForDataset(datasetId);
    const baseRows = rawStudyQuestions.length ? rawStudyQuestions : questions;
    if (baseRows.length) {
      setQuestions(randomizeQuestionList(baseRows, isRandomStudy, null));
    }
    setAnswers({});
    setResults({});
    setMarked({});
    setExamResult(null);
    setShowQuestionReview(false);
    setResultFilter('all');
    setCurrentIndex(0);
    setActivityStarted(true);
    setPendingResumePrompt(null);
    setShowResetConfirmModal(false);
    setSaveState(
      isRandomStudy
        ? '✓ Đã đặt lại đáp án & trộn đề ngẫu nhiên mới từ Câu 1'
        : '✓ Đã đặt lại đáp án theo thứ tự bộ đề từ Câu 1'
    );
  };

  const handleRestartStudyFresh = () => {
    studyCompletedRef.current = false;
    clearStudyStorageForDataset(datasetId);
    const baseRows = rawStudyQuestions.length ? rawStudyQuestions : questions;
    if (baseRows.length) {
      setQuestions(randomizeQuestionList(baseRows, isRandomStudy, null));
    }
    setAnswers({});
    setResults({});
    setMarked({});
    setExamResult(null);
    setShowQuestionReview(false);
    setResultFilter('all');
    setCurrentIndex(0);
    setActivityStarted(true);
    setPendingResumePrompt(null);
    setShowResetConfirmModal(false);
    setSaveState(
      isRandomStudy
        ? '✓ Đã bắt đầu mới (Trộn ngẫu nhiên câu hỏi & đáp án) từ Câu 1'
        : '✓ Đã bắt đầu mới (Theo thứ tự bộ đề) từ Câu 1'
    );
  };

  const resetSession = () => {
    studyCompletedRef.current = false;
    if (sessionId) {
      try {
        sessionStorage.removeItem(`${EXAM_SHUFFLE_CACHE_PREFIX}_${sessionId}`);
        localStorage.removeItem(`${EXAM_ORDER_STORAGE_PREFIX}_${sessionId}`);
      } catch (e) {}
    }
    if (datasetId) {
      clearStudyStorageForDataset(datasetId);
    }
    setAnswers({});
    setResults({});
    setMarked({});
    setExamResult(null);
    setShowQuestionReview(false);
    setResultFilter('all');
    setRemainingSeconds(DEFAULT_DURATION_SECONDS);
    setCurrentIndex(0);
    setSessionId(null);
    setSaveState('');
    setQuestions([]);
    setActivityStarted(false);
    setPendingResumePrompt(null);
    localStorage.removeItem(STORAGE_KEY);
    navigate('/', { replace: true });
  };

  const handleRetryCurrentTest = () => {
    setShowQuestionReview(false);
    setResultFilter('all');
    if (mode === 'exam') {
      if (isExamDisabled) {
        alert(
          `Bạn đã sử dụng hết số lượt thi thử được cấp (${remainingQuota?.used}/${remainingQuota?.total} lượt). Vui lòng chọn chế độ Luyện tập tự do hoặc liên hệ trung tâm để được cấp thêm lượt!`,
        );
        return;
      }
      setAnswers({});
      setResults({});
      setMarked({});
      setExamResult(null);
      setRemainingSeconds(DEFAULT_DURATION_SECONDS);
      setCurrentIndex(0);
      setSessionId(null);
      setSaveState('');
      startExam();
    } else {
      handleRestartStudyFresh();
    }
  };

  const handleSwitchExamAttempt = async (targetAttemptId) => {
    if (!targetAttemptId) return;
    try {
      setLoading(true);
      const attemptData = await getAttempt(targetAttemptId);
      await applyAttemptReview(attemptData, datasetId);
      setSearchParams(
        {
          dataset_id: attemptData.dataset_id || datasetId,
          mode: 'exam',
          attempt_id: String(targetAttemptId),
        },
        { replace: true },
      );
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Không tải được lịch sử bài thi.');
    } finally {
      setLoading(false);
    }
  };

  const datasetExamAttempts = useMemo(() => {
    if (!datasetId) return [];
    return (pastExamAttempts || []).filter(
      (att) => att.dataset_id === datasetId && att.mode !== 'study'
    );
  }, [pastExamAttempts, datasetId]);

  const startStudy = () => {
    studyCompletedRef.current = false;
    setMode('study');
    setSessionId(null);
    setExamResult(null);
    setShowQuestionReview(false);
    setResultFilter('all');
    setActivityStarted(true);
    setSaveState(isRandomStudy ? 'Chế độ: Trộn ngẫu nhiên' : 'Chế độ: Theo thứ tự đề');
    setSearchParams({ dataset_id: datasetId, mode: 'study', random: isRandomStudy ? '1' : '0' }, { replace: true });
  };

  const startExam = async () => {
    if (!datasetId) return;
    if (isExamDisabled) {
      alert(
        `Bạn đã sử dụng hết số lượt thi thử được cấp (${remainingQuota.used}/${remainingQuota.total} lượt). Vui lòng chọn chế độ Luyện tập tự do hoặc liên hệ trung tâm để được cấp thêm lượt!`,
      );
      return;
    }
    try {
      setLoading(true);
      setError('');
      const session = await startExamSession({
        dataset_id: datasetId,
        duration_minutes: DEFAULT_DURATION_SECONDS / 60,
        question_count: Math.min(50, selectedDataset?.total_questions || 50),
        pass_percent: 70,
        topic_slugs: topic ? [topic] : [],
        shuffle_questions: true,
      });
      setMode('exam');
      setActivityStarted(true);
      setSearchParams({ dataset_id: datasetId, mode: 'exam' }, { replace: true });
      applySession(session);
      setError('');
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.message || 'Không thể bắt đầu bài thi thử.';
      setError(errMsg);
      alert(errMsg);
      setActivityStarted(false);
      setMode('study');
    } finally {
      setLoading(false);
    }
  };

  const navigatorGridRef = useRef(null);

  const wrongQuestionIndices = useMemo(() => {
    if (!questions.length) return [];
    const indices = [];
    questions.forEach((q, idx) => {
      const qResult = results[q.id]
        || (examResult?.correct?.find((row) => row.question_id === q.id))
        || (examResult?.incorrect?.find((row) => row.question_id === q.id))
        || (examResult?.details?.find((row) => row.question_id === q.id));
      if (qResult?.is_correct === false) {
        indices.push(idx);
      }
    });
    return indices;
  }, [questions, results, examResult]);

  useEffect(() => {
    if (!navigatorGridRef.current) return;
    const activeCell = navigatorGridRef.current.querySelector('.nav-cell.active');
    if (activeCell && typeof activeCell.scrollIntoView === 'function') {
      activeCell.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentIndex]);

  const displayedReviewQuestions = useMemo(() => {
    if (!questions.length) return [];
    return questions
      .map((q, index) => {
        const qResult = results[q.id]
          || (examResult?.correct?.find((row) => row.question_id === q.id))
          || (examResult?.incorrect?.find((row) => row.question_id === q.id))
          || (examResult?.details?.find((row) => row.question_id === q.id));
        const isPass = qResult?.is_correct === true;
        return {
          question: q,
          result: qResult,
          displayIndex: index + 1,
          isPass,
        };
      })
      .filter(({ isPass }) => {
        if (resultFilter === 'correct') return isPass;
        if (resultFilter === 'incorrect') return !isPass;
        return true;
      });
  }, [questions, results, examResult, resultFilter]);

  return (
    <div className="quiz-shell">
      {/* MODAL NHẮC TIẾP TỤC BÀI LUYỆN TẬP HOẶC BẮT ĐẦU LẠI */}
      {pendingResumePrompt && (
        <div className="study-resume-modal-overlay" role="dialog" aria-modal="true">
          <div className="study-resume-card">
            <div className="resume-icon-badge">
              <AcademicCapIcon className="w-8 h-8" />
            </div>
            <h3>Tiếp tục bài luyện tập?</h3>
            <p className="resume-subtitle">
              Hệ thống tìm thấy tiến trình luyện tập của bạn đã được lưu:
            </p>

            <div className="resume-stats-box">
              <div className="resume-stat-item">
                <span className="stat-label">Đã hoàn thành:</span>
                <strong className="stat-val text-blue-600">
                  {new Set([
                    ...Object.keys(pendingResumePrompt.answers || {}),
                    ...Object.keys(pendingResumePrompt.results || {}),
                  ]).size} / {questions.length} câu
                </strong>
              </div>
              <div className="resume-stat-item">
                <span className="stat-label">Vị trí câu đang dừng:</span>
                <strong className="stat-val text-amber-600">
                  Câu {(() => {
                    if (pendingResumePrompt.currentQuestionId && questions.length) {
                      const idx = questions.findIndex((q) => (q.id || q.question_id) === pendingResumePrompt.currentQuestionId);
                      if (idx >= 0) return idx + 1;
                    }
                    return (pendingResumePrompt.currentIndex || 0) + 1;
                  })()}
                </strong>
              </div>
              <div className="resume-stat-item">
                <span className="stat-label">Chế độ phiên đã lưu:</span>
                <strong className="stat-val">
                  {pendingResumePrompt.isRandom ? 'Trộn ngẫu nhiên' : 'Theo thứ tự đề'}
                </strong>
              </div>
              <div className="resume-stat-item">
                <span className="stat-label">Thời gian lưu:</span>
                <span className="stat-time">{formatSavedDate(pendingResumePrompt.savedAt)}</span>
              </div>
            </div>

            <label className="resume-random-option">
              <input
                type="checkbox"
                checked={isRandomStudy}
                onChange={(e) => setIsRandomStudy(e.target.checked)}
              />
              <div>
                <strong>Trộn ngẫu nhiên câu hỏi và đáp án nếu bắt đầu lại từ đầu</strong>
                <span>
                  {isRandomStudy
                    ? 'Đang bật: Làm mới sẽ đảo vị trí câu hỏi và các phương án trả lời.'
                    : 'Đang tắt: Làm mới sẽ giữ nguyên thứ tự câu hỏi và đáp án theo bộ đề gốc.'}
                </span>
              </div>
            </label>

            <div className="resume-actions-group">
              <button
                type="button"
                className="btn-resume-continue"
                onClick={handleConfirmResumeStudy}
              >
                <ArrowRightIcon className="w-5 h-5 inline mr-1" />
                Làm tiếp (từ Câu {(() => {
                  if (pendingResumePrompt.currentQuestionId && questions.length) {
                    const idx = questions.findIndex((q) => (q.id || q.question_id) === pendingResumePrompt.currentQuestionId);
                    if (idx >= 0) return idx + 1;
                  }
                  return (pendingResumePrompt.currentIndex || 0) + 1;
                })()})
              </button>
              <button
                type="button"
                className="btn-resume-restart"
                onClick={handleRestartStudyFresh}
              >
                <ArrowPathIcon className="w-4 h-4 inline mr-1" />
                Bắt đầu lại từ đầu ({isRandomStudy ? 'Trộn ngẫu nhiên' : 'Theo thứ tự đề'})
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="quiz-topbar">
        <div className="quiz-topbar-inner">
          <div className="quiz-brand">
            <BrandLogo compact />
            <span className="quiz-brand-divider" />
            <div className="quiz-title">
              <h1>Ôn thi chứng chỉ GenAI Sao Việt</h1>
              <p>Hệ thống luyện tập và thi thử chuẩn hóa quốc tế</p>
            </div>
          </div>

          <nav className="quiz-topbar-nav" aria-label="Điều hướng ôn luyện">
            <Link
              to="/"
              state={{ page: 'dashboard' }}
              className="quiz-nav-item"
              onClick={(event) => {
                if (isActiveExam) {
                  event.preventDefault();
                  requestExamExit({
                    title: 'Tạm dừng & Về tổng quan?',
                    description: 'Hệ thống tự động lưu bài thi và thời gian còn lại để bạn tiếp tục bất kỳ lúc nào.',
                    action: () => navigate('/', { state: { page: 'dashboard' } }),
                  });
                } else if (isPracticeMode) {
                  if (examResult || studyCompletedRef.current) {
                    clearStudyStorageForDataset(datasetId);
                  } else {
                    saveStudyProgress(answers, results, marked, currentIndex);
                  }
                }
              }}
            >
              <HomeIcon />
              <span>Tổng quan</span>
            </Link>
            {!assignmentId && (
              <>
                <button
                  type="button"
                  className="quiz-nav-item quiz-nav-lobby"
                  onClick={() => {
                    requestExamExit({
                      title: 'Tạm dừng & Đổi bộ đề?',
                      description: 'Hệ thống tự động lưu bài thi và thời gian còn lại để bạn tiếp tục bất kỳ lúc nào.',
                      action: () => {
                        if (isPracticeMode) {
                          if (examResult || studyCompletedRef.current) {
                            clearStudyStorageForDataset(datasetId);
                          } else {
                            saveStudyProgress(answers, results, marked, currentIndex);
                          }
                        }
                        navigate('/', { state: { page: 'selection' } });
                      },
                    });
                  }}
                  title="Xem danh sách bộ đề hoặc chọn chế độ khác"
                >
                  <AcademicCapIcon />
                  <span>Bộ đề thi</span>
                </button>
                <button
                  type="button"
                  className="quiz-nav-item quiz-nav-history"
                  onClick={() => {
                    requestExamExit({
                      title: 'Tạm dừng & Xem lịch sử thi thử?',
                      description: 'Hệ thống tự động lưu bài thi và thời gian còn lại để bạn tiếp tục bất kỳ lúc nào.',
                      action: () => {
                        if (isPracticeMode) {
                          if (examResult || studyCompletedRef.current) {
                            clearStudyStorageForDataset(datasetId);
                          } else {
                            saveStudyProgress(answers, results, marked, currentIndex);
                          }
                        }
                        navigate('/', { state: { page: 'dashboard', openExamHistory: true } });
                      },
                    });
                  }}
                  title="Xem lại chi tiết các bài thi thử đã hoàn thành (Không trừ lượt thi)"
                >
                  <ClockIcon />
                  <span>Lịch sử thi thử</span>
                </button>
              </>
            )}
            {['admin', 'administrator', 'teacher', 'center'].includes((currentUser?.role || '').toLowerCase()) && (
              <Link
                to="/admin/accounts"
                className="quiz-nav-item quiz-nav-admin"
                onClick={(event) => {
                  if (isActiveExam) {
                    event.preventDefault();
                    requestExamExit({
                      title: 'Tạm dừng & Vào quản trị?',
                      description: 'Hệ thống tự động lưu bài thi và thời gian còn lại để bạn tiếp tục sau.',
                      action: () => navigate('/admin'),
                    });
                  }
                }}
              >
                <span>Quản trị</span>
              </Link>
            )}
          </nav>

          <div className="quiz-header-actions">
            {currentUser && (
              <>
                {remainingQuota && (
                  <span
                    className={`quiz-header-quota-pill ${isExamDisabled ? 'empty' : remainingQuota.isLow ? 'low' : 'ready'}`}
                    title={remainingQuota.isUnlimited ? 'Lượt thi vô hạn' : `Đã sử dụng ${remainingQuota.used}/${remainingQuota.total} lượt thi thử`}
                  >
                    <FireIcon />
                    <span>
                      <strong>{remainingQuota.isUnlimited ? 'Thi thử: Vô hạn' : `Còn ${remainingQuota.remaining} lượt`}</strong>
                      {!remainingQuota.isUnlimited && <small>Đã thi {remainingQuota.used}/{remainingQuota.total}</small>}
                    </span>
                  </span>
                )}
                <div className="quiz-avatar">
                  {(currentUser.full_name || currentUser.email || 'SV').charAt(0).toUpperCase()}
                </div>
                <span className="quiz-account-name">{currentUser.full_name || currentUser.email}</span>
                <button
                  aria-label="Đăng xuất"
                  className="quiz-btn icon-command"
                  onClick={() => requestExamExit({
                    title: 'Tạm dừng & Đăng xuất?',
                    description: 'Hệ thống tự động lưu bài thi và thời gian còn lại để bạn tiếp tục sau khi đăng nhập lại.',
                    action: logout,
                  })}
                  title="Đăng xuất"
                  type="button"
                >
                  <ArrowRightOnRectangleIcon />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {!activityStarted && !assignmentId ? (
        <main className="quiz-lobby">
          <section className="lobby-panel">
            <div className="lobby-heading">
              <span className="question-number">Sảnh ôn luyện</span>
              <h2>Chọn bộ đề và chế độ làm bài</h2>
              <p>Luyện tập dùng để học từng câu và xem giải thích ngay. Thi thử tính giờ 50 phút, rời bài sẽ nộp và kết thúc lượt thi.</p>
            </div>

            {/* QUOTA STATUS STRIP */}
            <div className="lobby-quota-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 18px', margin: '14px 0 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FireIcon style={{ width: '20px', height: '20px', color: isExamDisabled ? '#ef4444' : '#f59e0b' }} />
                <span style={{ fontSize: '13px', color: '#475569', fontWeight: 600 }}>Lượt thi thử còn lại:</span>
                <strong style={{ fontSize: '15px', color: isExamDisabled ? '#dc2626' : '#047857' }}>
                  {remainingQuota?.isUnlimited ? 'Vô hạn lượt' : `Còn ${remainingQuota?.remaining ?? 0} lượt`}
                </strong>
                {!remainingQuota?.isUnlimited && remainingQuota && (
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    (Đã thi {remainingQuota.used}/{remainingQuota.total} lượt)
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#15803d', background: '#dcfce7', padding: '3px 10px', borderRadius: '6px', fontWeight: 600 }}>
                  ✓ Luyện tập tự do không giới hạn (0 trừ lượt)
                </span>
              </div>
            </div>

            {isExamDisabled && (
              <div className="lobby-quota-alert" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', color: '#b91c1c' }}>
                <ExclamationTriangleIcon style={{ width: '24px', height: '24px', flexShrink: 0, color: '#dc2626' }} />
                <div>
                  <strong style={{ fontSize: '14px' }}>Bạn đã sử dụng hết số lượt thi thử ({remainingQuota.used}/{remainingQuota.total} lượt).</strong>
                  <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#b91c1c' }}>
                    Bạn vẫn có thể ôn tập tự do ở chế độ <strong>Luyện tập</strong>. Để mở thêm lượt thi thử tính giờ, vui lòng liên hệ trung tâm Sao Việt!
                  </p>
                </div>
              </div>
            )}

            <div className="lobby-grid">
              <div className="lobby-block">
                <h3>Bộ đề hiện có</h3>
                <div className="dataset-list">
                  {datasets.map((dataset) => (
                    <button
                      className={`dataset-card ${dataset.id === datasetId ? 'active' : ''}`}
                      key={dataset.id}
                      onClick={() => setDatasetId(dataset.id)}
                      type="button"
                    >
                      <strong>{dataset.title}</strong>
                      <span>{dataset.total_questions} câu hỏi</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="lobby-block">
                <h3>Chọn chế độ</h3>
                <label className={`lobby-random-toggle-box ${isRandomStudy ? 'is-random' : 'is-sequential'}`}>
                  <input
                    type="checkbox"
                    checked={isRandomStudy}
                    onChange={(e) => setIsRandomStudy(e.target.checked)}
                  />
                  <div>
                    <strong>
                      Trộn ngẫu nhiên câu hỏi và đáp án khi Luyện tập <em>(Mặc định)</em>
                    </strong>
                    <span>
                      {isRandomStudy
                        ? 'Đang bật: Đảo vị trí câu hỏi và các phương án lựa chọn mỗi lần luyện tập.'
                        : 'Đang tắt: Giữ nguyên thứ tự câu hỏi và các phương án trả lời theo bộ đề gốc.'}
                    </span>
                  </div>
                </label>
                <div className="lobby-actions">
                  <button className="mode-choice-card" disabled={!datasetId} onClick={startStudy} type="button">
                    <AcademicCapIcon />
                    <strong>Luyện tập ({isRandomStudy ? 'Trộn ngẫu nhiên' : 'Theo thứ tự đề'})</strong>
                    <span>Không tính giờ, có gợi ý và xem đúng/sai từng câu.</span>
                  </button>
                  <button
                    className={`mode-choice-card exam ${isExamDisabled ? 'disabled-quota' : ''}`}
                    disabled={!datasetId || isExamDisabled}
                    onClick={startExam}
                    type="button"
                    title={isExamDisabled ? 'Đã hết lượt thi thử' : 'Thi thử tính giờ 50 phút. Hệ thống tự động trộn ngẫu nhiên câu hỏi và đáp án.'}
                  >
                    <ClipboardDocumentCheckIcon />
                    <strong>
                      Thi thử {!remainingQuota?.isUnlimited && remainingQuota ? `(Còn ${remainingQuota.remaining} lượt)` : ''}
                    </strong>
                    <span>
                      {isExamDisabled
                        ? 'Đã hết lượt thi thử. Vui lòng chọn Luyện tập tự do hoặc liên hệ trung tâm.'
                        : `50 phút, tối đa ${Math.min(50, selectedDataset?.total_questions || 50)} câu. Rời bài là nộp bài.`}
                    </span>
                    <span className="lobby-exam-shuffle-note">
                      <ArrowsRightLeftIcon style={{ width: '13px', height: '13px' }} />
                      Lưu ý: Bài thi thử tự động trộn ngẫu nhiên câu hỏi và đáp án
                    </span>
                    {isExamDisabled && (
                      <span className="exam-locked-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '11px', fontWeight: 700, padding: '2px 8px', marginTop: '4px', width: 'fit-content' }}>
                        <LockClosedIcon style={{ width: '12px', height: '12px' }} /> Đã hết lượt thi thử
                      </span>
                    )}
                  </button>
                </div>

                {datasetExamAttempts.length > 0 && (
                  <div
                    style={{
                      marginTop: '16px',
                      background: '#f8fafc',
                      border: '1px solid #bfdbfe',
                      borderRadius: '12px',
                      padding: '14px 16px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                      <strong style={{ fontSize: '14px', color: '#1e3a8a', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <ClockIcon style={{ width: '16px', height: '16px', color: '#2563eb' }} />
                        Lịch sử các lần thi thử bộ đề này ({datasetExamAttempts.length} lần)
                      </strong>
                      <span style={{ fontSize: '12px', color: '#475569' }}>
                        Xem lại hoàn toàn miễn phí — không trừ lượt thi
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {datasetExamAttempts.map((att, idx) => {
                        const attemptNumber = datasetExamAttempts.length - idx;
                        const dateStr = att.submitted_at
                          ? new Date(att.submitted_at).toLocaleString('vi-VN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '';
                        return (
                          <button
                            key={att.attempt_id}
                            type="button"
                            onClick={() => handleSwitchExamAttempt(att.attempt_id)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid #93c5fd',
                              background: '#ffffff',
                              color: '#1e293b',
                              fontSize: '12.5px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ color: '#2563eb' }}>Lần #{attemptNumber}:</span>
                            <span
                              style={{
                                padding: '2px 7px',
                                borderRadius: '999px',
                                fontSize: '11.5px',
                                background: att.passed ? '#dcfce7' : '#fee2e2',
                                color: att.passed ? '#15803d' : '#b91c1c',
                              }}
                            >
                              {att.score_percent}% ({att.correct_questions}/{att.total_questions})
                            </span>
                            <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}>
                              {dateStr} · Xem lại →
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      ) : (
      <main className="quiz-main">
        <aside className="quiz-sidebar">
          <div className={`active-mode-card ${mode}`}>
            <div className="active-mode-icon">
              {mode === 'exam' ? <ClipboardDocumentCheckIcon /> : <AcademicCapIcon />}
            </div>
            <div>
              <div className="active-mode-badge-pill">{mode === 'exam' ? 'Chế độ: Thi thử' : mode === 'smart-review' ? 'Chế độ: Ôn thông minh' : 'Chế độ: Luyện tập'}</div>
              <strong>{mode === 'smart-review' ? 'Kho câu cần cải thiện' : (selectedDataset?.title || 'Bộ đề đang làm')}</strong>
              <span>
                {mode === 'exam'
                  ? '50 phút · Tự động trộn câu & đáp án'
                  : mode === 'smart-review'
                    ? 'Tối đa 20 câu · Không trừ lượt thi'
                    : (isRandomStudy ? 'Trộn ngẫu nhiên · Tự động lưu' : 'Theo thứ tự bộ đề · Tự động lưu')}
              </span>
            </div>
          </div>
          {mode === 'exam' && !examResult && <div className="timer-wrap"><ClockIcon /><div className="timer">{formatTime(remainingSeconds)}</div></div>}
          <div className="sidebar-mode-actions">
            {!examResult ? (
              mode === 'exam' ? (
                <div className="sidebar-action-row">
                  <button
                    className="sidebar-submit"
                    onClick={handleRequestSubmit}
                    disabled={submittingExam}
                    type="button"
                  >
                    <PaperAirplaneIcon />
                    <span>{submittingExam ? 'Đang chấm...' : 'Nộp bài'}</span>
                  </button>
                  <button
                    className={`sidebar-save ${savingManual ? 'saving' : ''} ${manualSaveNotice ? 'saved' : ''}`}
                    onClick={handleManualSaveExam}
                    disabled={savingManual}
                    type="button"
                    title="Lưu thủ công bài thi và thời gian còn lại"
                  >
                    <ClipboardDocumentCheckIcon />
                    <span>{savingManual ? 'Đang lưu...' : (manualSaveNotice || 'Lưu bài')}</span>
                  </button>
                </div>
              ) : (
                <div className="sidebar-action-row">
                  <button
                    className="sidebar-submit study-complete"
                    onClick={handleRequestSubmit}
                    disabled={submittingExam}
                    type="button"
                  >
                    <CheckCircleIcon />
                    <span>{submittingExam ? 'Đang lưu...' : (mode === 'smart-review' ? 'Kết thúc phiên' : 'Hoàn thành')}</span>
                  </button>
                  {isPracticeMode && answeredCount > 0 && (
                    <button
                      className="sidebar-reset sidebar-reset-fast"
                      onClick={handleRequestResetAllAnswers}
                      type="button"
                      title="Xóa các đáp án đã chọn và làm lại từ câu 1"
                    >
                      <ArrowPathIcon />
                      <span>Làm lại</span>
                    </button>
                  )}
                </div>
              )
            ) : (
              <div className="sidebar-action-row">
                <button
                  className="sidebar-submit study-complete"
                  onClick={() => {
                    if (showQuestionReview) {
                      setShowQuestionReview(false);
                    } else {
                      setCurrentIndex(wrongQuestionIndices[0] ?? 0);
                      setShowQuestionReview(true);
                    }
                  }}
                  type="button"
                >
                  <CheckCircleIcon />
                  <span>{showQuestionReview ? 'Bảng tổng kết' : 'Xem lại từng câu'}</span>
                </button>
                {isPracticeMode && (
                  <button
                    className="sidebar-reset sidebar-reset-fast"
                    onClick={handleRequestResetAllAnswers}
                    type="button"
                    title="Xóa các đáp án đã chọn và làm lại từ câu 1"
                  >
                    <ArrowPathIcon />
                    <span>Làm lại</span>
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="sidebar-progress-label">
            <strong>Danh sách câu hỏi ({questions.length})</strong>
            <span>Đã làm {answeredCount}/{questions.length}</span>
          </div>
          <div className="sidebar-progress"><span style={{ width: `${progressPercent}%` }} /></div>
          <div className="navigator-grid" ref={navigatorGridRef}>
            {questions.map((question, index) => {
              const qResult = results[question.id]
                || (examResult?.correct?.find((row) => row.question_id === question.id))
                || (examResult?.incorrect?.find((row) => row.question_id === question.id))
                || (examResult?.details?.find((row) => row.question_id === question.id));
              const isPass = qResult?.is_correct === true;
              const isFail = qResult?.is_correct === false;

              return (
                <button
                  className={[
                    'nav-cell',
                    index === currentIndex ? 'active' : '',
                    isPass ? 'correct' : '',
                    isFail ? 'incorrect' : '',
                    hasAnswer(question, answers[question.id]) ? 'answered' : '',
                    marked[question.id] ? 'marked' : '',
                  ].join(' ')}
                  key={question.id}
                  onClick={() => {
                    setCurrentIndex(index);
                    if (examResult) {
                      setShowQuestionReview(true);
                    }
                    if (typeof window !== 'undefined' && window.innerWidth <= 820) {
                      setTimeout(() => {
                        document.querySelector('.quiz-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 60);
                    }
                  }}
                  type="button"
                  title={`Câu ${index + 1}${isPass ? ': Đúng (Pass)' : isFail ? ': Chưa đúng' : ''}`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
          <div className="navigator-legend">
            {examResult || Object.values(results).some((r) => r?.is_correct !== undefined) ? (
              <>
                <span><i className="correct" />Câu đúng (Pass)</span>
                <span><i className="incorrect" />Câu sai</span>
                <span><i className="current" />Đang xem</span>
                <span><i className="answered" />Đã làm</span>
              </>
            ) : isPracticeMode ? (
              <><span><i className="correct" />Đúng</span><span><i className="incorrect" />Sai</span><span><i className="current" />Câu hiện tại</span><span><i />Chưa trả lời</span></>
            ) : (
              <><span><i className="answered" />Đã trả lời</span><span><i className="current" />Câu hiện tại</span><span><i />Chưa trả lời</span><span><i className="review" />Xem lại</span></>
            )}
          </div>
          {!sessionId && <details className="quiz-options">
            <summary>Tùy chọn nội dung & sắp xếp</summary>
            <div className="quiz-control">
              <label>Sắp xếp câu hỏi</label>
              <select
                value={isRandomStudy ? 'random' : 'sequential'}
                onChange={(event) => handleToggleStudyRandom(event.target.value === 'random')}
              >
                <option value="random">Trộn ngẫu nhiên</option>
                <option value="sequential">Theo thứ tự bộ đề</option>
              </select>
            </div>
            <div className="quiz-control"><label>Bộ đề</label><select value={datasetId} onChange={(event) => setDatasetId(event.target.value)}>{datasets.map((dataset) => <option key={dataset.id} value={dataset.id}>{dataset.title}</option>)}</select></div>
            <div className="quiz-control"><label>Chủ đề</label><select value={topic} onChange={(event) => setTopic(event.target.value)}><option value="">Tất cả chủ đề</option>{topics.map((row) => <option key={row.slug} value={row.slug}>{row.label} ({row.question_count})</option>)}</select></div>
          </details>}
        </aside>

        <section className="quiz-panel">
          {error && <div className="error-banner">{error}</div>}
          {loading && <div className="quiz-loading">Đang chuẩn bị câu hỏi...</div>}

          {!loading && currentQuestion && (!examResult || showQuestionReview) && (
            <>
              {examResult && (
                <div
                  className="review-mode-banner"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '10px',
                    padding: '12px 18px',
                    marginBottom: '18px',
                    flexWrap: 'wrap',
                    gap: '10px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>
                      Đang xem lại bài đã nộp ({mode === 'exam' ? 'Thi thử' : 'Ôn luyện'})
                    </span>
                    <span
                      style={{
                        background: currentReviewResult?.is_correct ? '#d1fae5' : '#fee2e2',
                        color: currentReviewResult?.is_correct ? '#065f46' : '#991b1b',
                        border: `1px solid ${currentReviewResult?.is_correct ? '#6ee7b7' : '#fca5a5'}`,
                        padding: '3px 12px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      {currentReviewResult?.is_correct ? '✓ Câu đúng (Pass)' : '✕ Câu sai'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="quiz-btn"
                    style={{ background: '#242c91', color: '#ffffff', padding: '6px 14px', fontSize: '13px', fontWeight: 600 }}
                    onClick={() => setShowQuestionReview(false)}
                  >
                    📊 Bảng tổng kết
                  </button>
                </div>
              )}
              <div className="question-toolbar">
                <div className="question-badges"><span className="question-number">Câu {currentIndex + 1} / {questions.length}</span><span className="question-type">{getQuestionTypeLabel(currentQuestion)}</span></div>
                <div className="question-tools">
                  {isPracticeMode && !currentResult && currentHint && !examResult && (
                    <button
                      className={`hint-button ${hintVisible ? 'active' : ''}`}
                      onClick={() => setHintVisible((value) => !value)}
                      title={hintVisible ? 'Ẩn gợi ý' : 'Xem gợi ý cho câu hỏi này'}
                      type="button"
                    >
                      <LightBulbIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} /> Gợi ý
                    </button>
                  )}
                  {isPracticeMode && currentResult && !examResult && (
                    <button
                      className="quiz-btn retry-question-tool"
                      onClick={handleRetryQuestion}
                      type="button"
                      title="Xóa đáp án câu này để chọn lại"
                    >
                      <ArrowPathIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} /> Chọn lại đáp án
                    </button>
                  )}
                  {mode === 'exam' && !examResult && (
                    <button className={`review-button ${marked[currentQuestion.id] ? 'active' : ''}`} onClick={() => setMarked((prev) => ({ ...prev, [currentQuestion.id]: !prev[currentQuestion.id] }))} type="button"><BookmarkIcon />{marked[currentQuestion.id] ? 'Đã đánh dấu' : 'Đánh dấu xem lại'}</button>
                  )}
                </div>
              </div>
              {isPracticeMode && !currentResult && hintVisible && currentHint && !examResult && (
                <div className="study-hint-panel">
                  <LightBulbIcon style={{ width: '22px', height: '22px', flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="study-hint-header-title">
                      <span>Gợi ý & Hướng dẫn tư duy:</span>
                    </div>
                    {renderStudyHint(currentHint, currentQuestion)}
                  </div>
                </div>
              )}
              <QuestionCard
                key={`${currentQuestion.id || currentIndex}-${currentIndex}`}
                question={currentQuestion}
                answer={currentAnswer}
                onAnswerChange={setCurrentAnswer}
                result={currentReviewResult}
                disabled={Boolean(examResult || currentReviewResult)}
              />
              <div className="question-actionbar">
                <button className="quiz-btn prev-question-btn" disabled={currentIndex === 0} onClick={() => setCurrentIndex((value) => Math.max(0, value - 1))} type="button"><ArrowLeftIcon /> Câu trước</button>
                <div className="answer-status">Đã làm <strong>{answeredCount}/{questions.length}</strong> câu</div>
                <div className="actionbar-right">
                  {examResult ? (
                    currentIndex < questions.length - 1 ? (
                      <button
                        className="quiz-btn next-question"
                        onClick={() => setCurrentIndex((val) => Math.min(questions.length - 1, val + 1))}
                        type="button"
                      >
                        Câu tiếp theo <ArrowRightIcon />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="quiz-btn next-question"
                        onClick={() => setShowQuestionReview(false)}
                      >
                        Xem bảng điểm <ArrowRightIcon />
                      </button>
                    )
                  ) : (
                    <>
                      {currentIndex < questions.length - 1 && (
                        <button className="quiz-btn next-question" onClick={handleNextQuestion} type="button">{getNextButtonLabel()} <ArrowRightIcon /></button>
                      )}
                      {currentIndex === questions.length - 1 && isPracticeMode && !currentResult && (
                        <button className="quiz-btn next-question" onClick={handleCheck} type="button">Xem đúng/sai <ArrowRightIcon /></button>
                      )}
                      {currentIndex === questions.length - 1 && (!isPracticeMode || currentResult) && (
                        <button 
                          className="quiz-btn submit-exam" 
                          onClick={handleRequestSubmit} 
                          disabled={submittingExam}
                          type="button"
                        >
                          <PaperAirplaneIcon /> {submittingExam ? 'Đang xử lý...' : (mode === 'exam' ? 'Nộp bài chấm điểm' : 'Hoàn thành bài luyện')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {!loading && examResult && !showQuestionReview && (
            <div className="result-panel">
              <div className="question-header">
                <div>
                  <div className="question-meta">Kết quả kiểm tra ({mode === 'exam' ? 'Thi thử' : 'Ôn luyện'})</div>
                  <div className="result-score">{examResult.score_percent}%</div>
                </div>
                <strong className={examResult.passed ? 'result-pass' : 'result-practice'}>
                  {examResult.passed ? '✓ ĐẠT MỤC TIÊU (PASS)' : '✕ CẦN LUYỆN THÊM'}
                </strong>
              </div>

              {/* STATS SUMMARY CARDS */}
              <div className="result-stats-summary" style={{ display: 'flex', gap: '14px', margin: '16px 0 20px', flexWrap: 'wrap' }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 18px', flex: '1 1 120px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Tổng số câu</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#1e293b' }}>{examResult.total_questions}</div>
                </div>
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '10px', padding: '12px 18px', flex: '1 1 120px' }}>
                  <div style={{ fontSize: '12px', color: '#047857', fontWeight: 600 }}>Số câu đúng (Pass)</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#059669' }}>{examResult.correct_questions} câu</div>
                </div>
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 18px', flex: '1 1 120px' }}>
                  <div style={{ fontSize: '12px', color: '#b91c1c', fontWeight: 600 }}>Số câu sai</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#dc2626' }}>{Math.max(0, examResult.total_questions - examResult.correct_questions)} câu</div>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 18px', flex: '1 1 120px' }}>
                  <div style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: 600 }}>Mốc đạt chuẩn</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#2563eb' }}>{examResult.pass_percent}%</div>
                </div>
              </div>

              {mode === 'smart-review' && examResult.smart_review && (
                <div className="smart-review-result" aria-label="Kết quả ôn tập thông minh">
                  <div><span>Đúng trong phiên</span><strong>{examResult.smart_review.correct}</strong></div>
                  <div><span>Tiến gần nắm vững</span><strong>{examResult.smart_review.progressed}</strong></div>
                  <div><span>Vừa nắm vững</span><strong>{examResult.smart_review.mastered}</strong></div>
                  <div><span>Còn cần ôn</span><strong>{examResult.smart_review.remaining}</strong></div>
                </div>
              )}

              {/* ACTION: REVIEW QUESTION BY QUESTION */}
              <div style={{ marginBottom: '22px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  className="quiz-btn"
                  onClick={() => {
                    setCurrentIndex(0);
                    setShowQuestionReview(true);
                  }}
                  style={{
                    background: '#047857',
                    color: '#ffffff',
                    padding: '11px 22px',
                    fontWeight: 700,
                    fontSize: '14.5px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 6px rgba(4, 120, 87, 0.25)',
                    cursor: 'pointer',
                  }}
                >
                  <CheckCircleIcon style={{ width: '20px', height: '20px' }} /> Xem lại chi tiết từng câu trong bài thi
                </button>
                {mode !== 'smart-review' && (
                  <button
                    type="button"
                    className="quiz-btn next-question"
                    onClick={handleRetryCurrentTest}
                    style={{
                      padding: '11px 20px',
                      fontWeight: 700,
                      fontSize: '14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    <ArrowPathIcon style={{ width: '18px', height: '18px' }} />
                    {mode === 'exam' ? 'Thi thử lại đề này (Trừ 1 lượt)' : 'Làm lại bài này'}
                  </button>
                )}
              </div>

              {mode === 'exam' && datasetExamAttempts.length > 0 && (
                <div
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '10px',
                    padding: '14px 16px',
                    marginBottom: '20px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                    <strong style={{ fontSize: '14.5px', color: '#1e293b', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <ClockIcon style={{ width: '17px', height: '17px', color: '#242c91' }} />
                      Lịch sử các lần thi thử bộ đề này ({datasetExamAttempts.length} lần)
                    </strong>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      Xem lại lịch sử hoàn toàn miễn phí — không trừ lượt thi thử
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {datasetExamAttempts.map((att, idx) => {
                      const isCurrentAttempt = String(att.attempt_id) === String(examResult?.attempt_id);
                      const attemptNumber = datasetExamAttempts.length - idx;
                      const dateStr = att.submitted_at
                        ? new Date(att.submitted_at).toLocaleString('vi-VN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '';
                      return (
                        <button
                          key={att.attempt_id}
                          type="button"
                          onClick={() => {
                            if (!isCurrentAttempt) {
                              handleSwitchExamAttempt(att.attempt_id);
                            }
                          }}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '4px',
                            padding: '9px 14px',
                            borderRadius: '8px',
                            border: isCurrentAttempt ? '2px solid #242c91' : '1px solid #cbd5e1',
                            background: isCurrentAttempt ? '#eef2ff' : '#ffffff',
                            cursor: isCurrentAttempt ? 'default' : 'pointer',
                            textAlign: 'left',
                            minWidth: '175px',
                            boxShadow: isCurrentAttempt ? '0 2px 6px rgba(36, 44, 145, 0.14)' : 'none',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '8px' }}>
                            <strong style={{ fontSize: '13px', color: isCurrentAttempt ? '#242c91' : '#1e293b' }}>
                              Lần thi #{attemptNumber} {isCurrentAttempt ? '(Đang xem)' : ''}
                            </strong>
                            <span
                              style={{
                                fontSize: '11.5px',
                                fontWeight: 700,
                                padding: '2px 7px',
                                borderRadius: '999px',
                                background: att.passed ? '#dcfce7' : '#fee2e2',
                                color: att.passed ? '#15803d' : '#b91c1c',
                              }}
                            >
                              {att.score_percent}% ({att.correct_questions}/{att.total_questions})
                            </span>
                          </div>
                          <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                            {dateStr}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* FILTER TABS */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
                  Chi tiết câu hỏi & Lời giải:
                </h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setResultFilter('all')}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: resultFilter === 'all' ? '#242c91' : '#cbd5e1',
                      background: resultFilter === 'all' ? '#242c91' : '#ffffff',
                      color: resultFilter === 'all' ? '#ffffff' : '#334155',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Tất cả ({questions.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setResultFilter('correct')}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: resultFilter === 'correct' ? '#059669' : '#a7f3d0',
                      background: resultFilter === 'correct' ? '#059669' : '#ecfdf5',
                      color: resultFilter === 'correct' ? '#ffffff' : '#047857',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    ✓ Câu đúng ({examResult.correct_questions || (examResult.correct || []).length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setResultFilter('incorrect')}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: resultFilter === 'incorrect' ? '#dc2626' : '#fecaca',
                      background: resultFilter === 'incorrect' ? '#dc2626' : '#fef2f2',
                      color: resultFilter === 'incorrect' ? '#ffffff' : '#b91c1c',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    ✕ Câu sai ({Math.max(0, examResult.total_questions - examResult.correct_questions)})
                  </button>
                </div>
              </div>

              {/* LIST OF REVIEW QUESTIONS */}
              <div className="review-question-list">
                {displayedReviewQuestions.length === 0 ? (
                  <p style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Không có câu hỏi nào trong mục này.</p>
                ) : (
                  displayedReviewQuestions.map(({ question, result: qResult, displayIndex, isPass }) => {
                    const contextText = question?.context_text || question?.prompt?.context || '';
                    const qAnswer = answers[question.id];
                    return (
                      <div
                        className={`feedback-box ${isPass ? 'correct' : 'incorrect'}`}
                        key={question.id}
                        style={{ marginBottom: '16px', borderRadius: '8px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                          <strong style={{ fontSize: '15px' }}>Câu {displayIndex}</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              style={{
                                background: isPass ? '#10b981' : '#ef4444',
                                color: '#ffffff',
                                padding: '3px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 700,
                              }}
                            >
                              {isPass ? '✓ ĐÚNG (PASS)' : '✕ CHƯA ĐÚNG'}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const idx = questions.findIndex((q) => q.id === question.id);
                                if (idx !== -1) setCurrentIndex(idx);
                                setShowQuestionReview(true);
                              }}
                              style={{
                                background: '#ffffff',
                                border: `1px solid ${isPass ? '#10b981' : '#ef4444'}`,
                                color: isPass ? '#047857' : '#b91c1c',
                                borderRadius: '6px',
                                padding: '3px 10px',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              Xem chi tiết câu này →
                            </button>
                          </div>
                        </div>
                        {contextText && (
                          <div className="review-context-text" style={{ margin: '6px 0 8px', fontStyle: 'italic', color: '#475569' }}>
                            {contextText}
                          </div>
                        )}
                        <div style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14.5px', color: '#1e293b' }}>
                          {question?.question_text}
                        </div>
                        <MistakeCorrectionPanel
                          question={question}
                          answer={qAnswer}
                          result={qResult}
                        />
                      </div>
                    );
                  })
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                {mode === 'smart-review' ? (
                  <button className="quiz-btn next-question" onClick={() => navigate(0)} type="button"><ArrowPathIcon /> Ôn tiếp câu còn yếu</button>
                ) : (
                  <button className="quiz-btn next-question" onClick={handleRetryCurrentTest} type="button"><ArrowPathIcon /> Làm lại bài này</button>
                )}
                <Link
                  to="/"
                  className="quiz-btn"
                  onClick={() => {
                    if (isPracticeMode) {
                      clearStudyStorageForDataset(datasetId);
                    }
                  }}
                  style={{
                    textDecoration: 'none',
                    background: '#242c91',
                    color: '#ffffff',
                    border: '1px solid #242c91',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 700,
                  }}
                >
                  <HomeIcon style={{ width: '18px', height: '18px' }} /> Về sảnh luyện thi
                </Link>
              </div>
            </div>
          )}
        </section>
      </main>
      )}

      {examExitIntent && (
        <div className="submit-modal-overlay" onClick={() => !submittingExam && !loading && setExamExitIntent(null)}>
          <div className="submit-modal-card exam-exit-card" onClick={(e) => e.stopPropagation()}>
            <div className="submit-modal-header">
              <div className="submit-modal-icon" style={{ background: '#e0e7ff', color: '#4338ca' }}>
                <InformationCircleIcon className="w-6 h-6" />
              </div>
              <div>
                <h3>{examExitIntent.title || 'Tạm dừng & Quay lại sảnh?'}</h3>
                <p>Bài thi thử đang tính giờ</p>
              </div>
            </div>
            <div className="submit-modal-body">
              <div className="submit-modal-stats">
                <div className="stat-box answered">
                  <span className="stat-num">{answeredCount}</span>
                  <span className="stat-lbl">Đã trả lời</span>
                </div>
                <div className="stat-box remaining">
                  <span className="stat-num">{questions.length - answeredCount}</span>
                  <span className="stat-lbl">Chưa trả lời</span>
                </div>
                <div className="stat-box total">
                  <span className="stat-num">{formatTime(remainingSeconds)}</span>
                  <span className="stat-lbl">Còn lại</span>
                </div>
              </div>
              <div
                className="submit-modal-warning"
                style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  color: '#166534',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  fontSize: '13px',
                  lineHeight: 1.5,
                  margin: '14px 0 4px',
                  textAlign: 'left',
                }}
              >
                ✓ <strong>Bài thi KHÔNG bị đóng:</strong> Hệ thống tự động lưu {answeredCount} câu đã làm và thời gian còn lại. Bạn có thể quay lại sảnh và tiếp tục thi bất kỳ lúc nào mà không bị mất lượt thi!
              </div>
            </div>
            <div className="submit-modal-actions" style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
              <button
                type="button"
                className="btn-modal-confirm"
                style={{
                  background: '#242c91',
                  color: '#ffffff',
                  border: 'none',
                  padding: '12px 18px',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: 'pointer',
                  width: '100%',
                }}
                disabled={loading || submittingExam}
                onClick={handleSaveAndExitExam}
              >
                {loading ? 'Đang lưu tiến trình...' : 'Lưu & Về sảnh (Không đóng bài)'}
              </button>
              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                <button
                  type="button"
                  className="btn-modal-cancel"
                  style={{ flex: 1, padding: '10px' }}
                  disabled={loading || submittingExam}
                  onClick={() => setExamExitIntent(null)}
                >
                  Ở lại làm tiếp
                </button>
                <button
                  type="button"
                  className="btn-modal-confirm danger"
                  style={{
                    flex: 1,
                    background: '#fff',
                    color: '#dc2626',
                    border: '1px solid #fca5a5',
                    padding: '10px',
                  }}
                  disabled={loading || submittingExam}
                  onClick={confirmExamExit}
                  title="Chấm điểm các câu đã làm và kết thúc lượt thi ngay"
                >
                  {submittingExam ? 'Đang nộp...' : 'Nộp bài ngay'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSubmitConfirmModal && (
        <div className="submit-modal-overlay" onClick={() => !submittingExam && setShowSubmitConfirmModal(false)}>
          <div className="submit-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="submit-modal-header">
              <div className="submit-modal-icon">
                <PaperAirplaneIcon className="w-6 h-6" />
              </div>
              <div>
                <h3>Xác nhận nộp bài</h3>
                <p>{mode === 'exam' ? 'Bài thi thử tính giờ' : 'Bài luyện tập'}</p>
              </div>
            </div>
            <div className="submit-modal-body">
              <div className="submit-modal-stats">
                <div className="stat-box answered">
                  <span className="stat-num">{answeredCount}</span>
                  <span className="stat-lbl">Đã trả lời</span>
                </div>
                <div className="stat-box remaining">
                  <span className="stat-num">{questions.length - answeredCount}</span>
                  <span className="stat-lbl">Chưa trả lời</span>
                </div>
                <div className="stat-box total">
                  <span className="stat-num">{questions.length}</span>
                  <span className="stat-lbl">Tổng số câu</span>
                </div>
              </div>
              {questions.length - answeredCount > 0 ? (
                <div className="submit-modal-warning">
                  ⚠️ <strong>Lưu ý:</strong> Bạn còn <strong>{questions.length - answeredCount}</strong> câu chưa trả lời. Những câu này sẽ được tính là 0 điểm. Bạn có chắc chắn muốn nộp bài để chấm điểm ngay?
                </div>
              ) : (
                <div className="submit-modal-success">
                  ✨ Bạn đã hoàn thành tất cả <strong>{questions.length}</strong> câu hỏi! Bạn đã sẵn sàng nộp bài để xem kết quả chi tiết?
                </div>
              )}
            </div>
            <div className="submit-modal-actions">
              <button
                type="button"
                className="btn-modal-cancel"
                disabled={submittingExam}
                onClick={() => setShowSubmitConfirmModal(false)}
              >
                Quay lại làm tiếp
              </button>
              <button
                type="button"
                className="btn-modal-confirm"
                disabled={submittingExam}
                onClick={performSubmitExam}
              >
                {submittingExam ? 'Đang chấm điểm...' : 'Xác nhận nộp bài'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showResetConfirmModal && (
        <div className="submit-modal-overlay" onClick={() => setShowResetConfirmModal(false)}>
          <div className="submit-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="submit-modal-header">
              <div className="submit-modal-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                <ArrowPathIcon className="w-6 h-6" />
              </div>
              <div>
                <h3>Làm lại từ đầu</h3>
                <p>Chế độ: Luyện tập tự do</p>
              </div>
            </div>
            <div className="submit-modal-body">
              <div className="submit-modal-stats">
                <div className="stat-box answered">
                  <span className="stat-num">{answeredCount}</span>
                  <span className="stat-lbl">Đã trả lời</span>
                </div>
                <div className="stat-box remaining">
                  <span className="stat-num">{questions.length - answeredCount}</span>
                  <span className="stat-lbl">Chưa làm</span>
                </div>
                <div className="stat-box total">
                  <span className="stat-num">{questions.length}</span>
                  <span className="stat-lbl">Tổng số câu</span>
                </div>
              </div>
              <div className="submit-modal-warning" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>
                🔄 <strong>Xác nhận làm lại:</strong> Thao tác này sẽ xóa các đáp án đã chọn trong lượt luyện hiện tại và đưa bạn quay về <strong>Câu 1</strong>.
              </div>
            </div>
            <div className="submit-modal-actions">
              <button
                type="button"
                className="btn-modal-cancel"
                onClick={() => setShowResetConfirmModal(false)}
              >
                Hủy
              </button>
              <button
                type="button"
                className="btn-modal-confirm"
                style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)' }}
                onClick={executeResetAllAnswers}
              >
                Làm lại từ đầu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizApp;
